import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// English translations
const en = {
  translation: {
    // Navigation
    dashboard: 'Dashboard',
    characterLab: 'Character Lab',
    plotForge: 'Plot Forge',
    theWeb: 'The Web',
    worldBuilding: 'World Building',
    logout: 'Logout',
    upgrade: 'Upgrade to Pro',
    settings: 'Settings',
    
    // Character Levels
    level0Title: 'Level 0: Identity',
    level0Subtitle: 'The Foundation',
    level1Title: 'Level 1: The Internal Blueprint',
    level1Subtitle: 'Soul & Origin',
    level2Title: 'Level 2: The World & Introduction',
    level2Subtitle: 'Setup',
    level3Title: 'Level 3: The Pressure Cooker',
    level3Subtitle: 'The Journey',
    level4Title: 'Level 4: The Final Reckoning',
    level4Subtitle: 'Resolution',
    
    // Form Labels
    name: 'Name',
    age: 'Age',
    gender: 'Gender',
    ethnicity: 'Ethnicity',
    shortBiography: 'Short Biography',
    role: 'Role',
    arcType: 'Arc Type',
    
    // Role Options
    protagonist: 'Protagonist',
    antagonist: 'Antagonist',
    supporting: 'Supporting',
    
    // Arc Types
    changePositive: 'Change (Positive)',
    fallNegative: 'Fall (Negative)',
    flat: 'Flat',
    
    // Arc Helper
    arcHelper: 'Arc Helper',
    keyElements: 'Key Elements',
    proTips: 'Pro Tips',
    
    // Level 1 Questions
    theLie: 'The Lie',
    theTruth: 'The Truth',
    theGhost: 'The Ghost',
    wants: 'Wants',
    needs: 'Needs',
    corruption: 'The Corruption',
    mission: 'Mission',
    
    // Character Web
    characterRelationshipMatrix: 'Character Relationship Matrix',
    addRelationship: 'Add Relationship',
    characterA: 'Character A',
    characterB: 'Character B',
    moralDifference: 'Moral Difference',
    tacticalDifference: 'Tactical Difference',
    centralArgument: 'Central Argument',
    conflictGauge: 'Conflict Gauge',
    
    // World Building
    locations: 'Locations',
    costumes: 'Costumes',
    props: 'Props',
    description: 'Description',
    vibe: 'Vibe',
    plotSignificance: 'Plot Significance',
    aesthetic: 'Aesthetic',
    evolution: 'Evolution',
    owner: 'Owner',
    symbolicValue: 'Symbolic Value',
    function: 'Function',
    
    // Buttons
    save: 'Save',
    saveProgress: 'Save Progress',
    saveStatus_saving: 'Saving…',
    saveStatus_dirty: 'Unsaved changes…',
    saveStatus_notSaving: 'NOT SAVING — copy your work',
    saveStatus_retrying: 'Save failed — retrying…',
    saveStatus_saved: 'Saved',
    saveStatus_savedAt: 'Saved {{time}}',
    saveStatus_conflict: 'This project was changed elsewhere — reload this tab to continue',
    milestone_developed: '{{name}} is fully developed.',
    char_completeLevel: 'Complete Level {{n}}',
    scene_unassigned: 'Unassigned',
    char_depthDeveloped: '{{levels}} developed',
    autosave_navBlocked: "Your edits haven't saved yet — copy your work, then try again",
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    newCharacter: 'New Character',
    newPlot: 'New Plot',
    exportPdf: 'Export PDF',
    stressTest: 'Stress Test',
    aiMentor: 'AI Mentor',
    
    // Story Auditor
    storyAuditor: 'Story Auditor',
    runStressTest: 'Run Stress Test',
    storyHealthScore: 'Story Health Score',
    issuesFound: 'Issues Found',
    suggestions: 'Suggestions',
    strengths: 'Strengths',
    
    // Payment
    upgradeToPro: 'Upgrade to Pro',
    monthly: 'Monthly',
    yearly: 'Yearly',
    perMonth: '/month',
    perYear: '/year',
    subscribe: 'Subscribe',
    
    // Messages
    loading: 'Loading...',
    saved: 'Saved successfully',
    error: 'Something went wrong',
    proFeature: 'Pro Feature',

    // Forge Guide toggle
    showForgeGuide: 'Show Forge Guide',
    hideForgeGuide: 'Hide Forge Guide',

    // Forge Guide beat texts
    forgeGuide_firstFrame: "This is the very first impression of your world. Show us a snapshot of your protagonist's life BEFORE everything changes. It should be a visual, emotional mirror of where they end up — the audience won't know it yet, but you do. Ask yourself: what does normal look like for this person, and how far are they from who they need to become?",
    forgeGuide_coreTruth: "Somewhere in your opening act, a character — often not the hero — says something that is the thematic spine of your entire story. Your protagonist will probably disagree with it right now. By the end, they'll understand it completely. What is the one truth your story is trying to prove?",
    forgeGuide_worldBefore: "Introduce your protagonist in their element. We need to see their world, their relationships, their flaws, and their unfulfilled need. Six things must be established here: character, stakes, genre, tone, the flaw your hero must overcome, and the world that is about to be disrupted. Take your time — this is the last moment of peace.",
    forgeGuide_spark: "Something happens that the protagonist did NOT cause and cannot ignore. This is the event that knocks on the door of their ordinary world and demands an answer. It should feel both surprising and inevitable. It is not a decision — it is the universe making a move. What happens TO your hero that forces the story to begin?",
    forgeGuide_crossroads: "Your hero knows the Spark happened, but they're not ready to act. This is the internal wrestling match — should I go or should I stay? The longer and more painful this debate, the more the audience invests. Your hero needs a push — a final piece of information, a threat, or a moment of courage — to finally commit. What is stopping them, and what finally moves them?",
    forgeGuide_leap: "Your protagonist makes an active, conscious choice to enter a new world — literally or metaphorically. The story of Act One is over. A new story begins. This should feel like a point of no return. The audience should sense that nothing will ever be the same. Where does your hero cross the threshold, and what do they leave behind?",
    forgeGuide_heartThread: "This is your emotional subplot — usually a relationship that carries the theme. While the A story is about what your hero WANTS, the Heart Thread is about what they NEED. Often a mentor, love interest, or unlikely friendship. This relationship will teach your protagonist the Core Truth by the end. Who is this person, and what do they represent that your hero is missing?",
    forgeGuide_promise: "This is why people bought a ticket. The fun, the thrills, the romance, the scares — whatever your genre promises, deliver it here. Your protagonist is in the new world and exploring it. Things aren't going perfectly, but they're not falling apart yet either. This is the most entertaining section of your film. Are you keeping the promise your genre made to the audience?",
    forgeGuide_turningTide: "Halfway through your story, something major shifts. Either your hero has a false victory (things seem great but are about to fall apart) or a false defeat (things seem terrible but the hero finds their fight). The stakes double here. Your protagonist must stop reacting and start driving the story forward. What changes at the midpoint that makes the second half a completely different kind of story?",
    forgeGuide_wallsClosing: "Your hero is pursuing their goal, but now every force — internal and external — is pushing back. Doubt, enemies, bad luck, betrayal. The tension must feel suffocating. The audience should genuinely wonder if your hero can make it. This is not random hardship — every obstacle should expose a deeper flaw your hero hasn't dealt with yet. Who or what is closing in, and why does it hurt the most right now?",
    forgeGuide_breakingPoint: "Your hero has lost. Not just the external goal — they have lost the thing that mattered most emotionally. A relationship, a belief, a mentor, a part of themselves. This is the lowest moment in the story. It should feel final. The audience must believe, even for a moment, that there is no way back. What is the single worst thing that could happen to your hero right now — and make it happen.",
    forgeGuide_hollow: "After the Breaking Point, your hero sits in the wreckage. This is a moment of stillness before the storm. They must confront the lie they've been living and choose — consciously and painfully — to embrace the Core Truth. This is the soul of your story. No one can save them here. They must save themselves. What does your hero realise in their darkest, most silent moment?",
    forgeGuide_rising: "Your hero emerges from The Hollow transformed. Armed with the truth, they formulate a final plan. Old tools won't work — they must use who they've BECOME, not who they were. The A story and Heart Thread begin to converge. The final battle is coming, and for the first time, your hero is truly ready. What is the plan, and how does it reflect their growth?",
    forgeGuide_reckoning: "Your hero executes the plan in a climax that tests every single thing they've learned. The theme is proven, the flaw is conquered, and the antagonist — internal or external — is faced head on. Every major character's story should resolve here. Make it earn every minute the audience has invested. Does your ending feel both surprising AND inevitable?",
    forgeGuide_lastFrame: "A final image that mirrors the First Frame — but everything has changed. We see the world AFTER. Your protagonist is transformed. The promise of the Core Truth has been fulfilled. This image should linger. It should feel like the last note of a song that resolves perfectly. What does your hero's world look like now that they have become who they were always meant to be?",

    // ── Common ──
    saving: 'Saving...',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    previous: 'Previous',
    next: 'Next',
    back: 'Back',
    generating: 'Generating...',

    // ── Dashboard ──
    dash_title: 'My Projects',
    dash_newProject: 'New Project',
    dash_noProjects: 'No projects yet — create your first below',
    dash_emptyTitle: 'No Projects Yet',
    dash_emptyDesc: 'Create your first project to start building your story.',
    dash_createFirst: 'Create Your First Project',
    dash_lastEdited: 'Last edited {{date}}',
    dash_progress: '{{pct}}% complete',
    dash_projects_one: '1 project',
    dash_projects_other: '{{count}} projects',

    // ── Sidebar ──
    sidebar_back: 'Back',
    sidebar_backToProjects: 'Back to My Projects',
    sidebar_plot: 'Plot',
    sidebar_chars: 'Chars',
    sidebar_scenes: 'Scenes',
    sidebar_script: 'Script',
    sidebar_world: 'World',
    sidebar_web: 'Web',
    sidebar_bible: 'Bible',
    sidebar_mentor: 'Mentor',
    sidebar_char: 'Char',
    sidebar_locked: 'Requires Indie plan or above',

    // ── Navigation ──
    nav_backToDashboard: 'Dashboard',
    nav_backToWorkspace: 'Back to Workspace',
    nav_backToPlot: 'Back to Plot',

    // ── Plot Act titles ──
    plotAct1Title: 'Act I: Departure',
    plotAct1Sub: 'Setup',
    plotAct2Title: 'Act II: Confrontation',
    plotAct2Sub: 'Rising Action',
    plotAct3Title: 'Act III: Resolution',
    plotAct3Sub: 'Climax & Resolution',

    // ── Plot level UI ──
    plot_overallProgress: 'Overall Progress',
    plot_beatOf: 'Beat {{current}} of {{total}}',
    plot_buildScenes: 'Build Scenes',
    plot_complete: 'Complete Plot',
    plot_nextBeat: 'Next Beat',

    // ── Beat placeholders ──
    beatPh_opening_image: 'The snapshot of your story world before change begins',
    beatPh_setup: 'Introduce the lead, their world, and initial relationships',
    beatPh_theme_stated: 'A hint of the lesson or truth the lead will learn',
    beatPh_catalyst: 'The inciting incident that disrupts the normal world',
    beatPh_debate: 'The lead hesitates or questions whether to commit to the journey',
    beatPh_break_into_two: 'The lead commits and enters a new world or situation',
    beatPh_b_story: 'Introduction of a subplot or relationship that reflects the theme',
    beatPh_fun_and_games: 'The promise of the premise — what the audience came for',
    beatPh_midpoint: 'A major victory or defeat that raises the stakes',
    beatPh_bad_guys_close_in: 'External and internal forces tighten around the lead',
    beatPh_all_is_lost: 'The lowest point — false defeat or major setback',
    beatPh_dark_night: 'The lead processes their failure before rebirth',
    beatPh_break_into_three: 'The lead finds the solution or new approach',
    beatPh_finale: 'The final confrontation where the lead proves their transformation',
    beatPh_final_image: 'Mirror of opening image showing how much has changed',
    beatPh_obstacle: 'The main conflict or challenge intensifies',
    beatPh_escalation: 'Stakes rise, complications mount',

    // ── Character Level titles ──
    charLevel0Title: 'Level 0: Identity',
    charLevel0Sub: 'The Foundation',
    charLevel1Title: 'Level 1: The Internal Blueprint',
    charLevel1Sub: 'Soul & Origin',
    charLevel2Title: 'Level 2: The World & Introduction',
    charLevel2Sub: 'Setup',
    charLevel3Title: 'Level 3: The Pressure Cooker',
    charLevel3Sub: 'The Journey',
    charLevel4Title: 'Level 4: The Final Reckoning',
    charLevel4Sub: 'Resolution',
    charAntag1Title: 'Level 1: Motivation & Internal Logic',
    charAntag1Sub: 'The Why They Fight',
    charAntag2Title: 'Level 2: Protagonist Connection',
    charAntag2Sub: 'The Opposition',
    charAntag3Title: 'Level 3: Power & Presence',
    charAntag3Sub: 'Threat & Agency',
    charAntag4Title: 'Level 4: Complexity & Humanity',
    charAntag4Sub: 'Beyond the Villain',
    charSupp1Title: 'Level 1: The Why',
    charSupp1Sub: 'Purpose & Role',
    charSupp2Title: 'Level 2: The Who',
    charSupp2Sub: 'Character & Depth',
    charSupp3Title: 'Level 3: The How',
    charSupp3Sub: 'Voice & Presence',
    charSupp4Title: 'Level 4: The Growth',
    charSupp4Sub: 'Arc & Impact',

    // ── Character question labels ──
    qLabel_name: 'Name',
    qLabel_age: 'Age',
    qLabel_gender: 'Gender',
    qLabel_ethnicity: 'Ethnicity',
    qLabel_biography: 'Short Biography',
    qLabel_the_lie: 'The Lie',
    qLabel_the_truth: 'The Truth',
    qLabel_the_ghost: 'The Ghost (Optional for Short Films)',
    qLabel_wants: 'Wants',
    qLabel_needs: 'Needs',
    qLabel_needs_ignored: 'Needs (Ignored)',
    qLabel_corruption: 'The Corruption',
    qLabel_mission: 'Mission',
    qLabel_hero_narrative: 'Their Hero Narrative',
    qLabel_tangible_goal: 'Tangible Goal',
    qLabel_human_roots: 'Human Roots',
    qLabel_internal_logic: 'Internal Logic',
    qLabel_defined_role: 'Defined Role',
    qLabel_plot_impact: 'Plot Impact',
    qLabel_the_trade: 'The Trade',
    qLabel_normal_world: 'The Normal World',
    qLabel_persona: 'Persona',
    qLabel_core_trait: 'Core Trait',
    qLabel_characteristic_moment: 'Characteristic Moment',
    qLabel_direct_conflict: 'Direct Conflict',
    qLabel_mirror_effect: 'Mirror Effect',
    qLabel_targeted_pressure: 'Targeted Pressure',
    qLabel_the_wound: 'The Wound',
    qLabel_offscreen_goal: 'Offscreen Goal',
    qLabel_personal_stakes: 'Personal Stakes',
    qLabel_the_secret: 'The Secret',
    qLabel_moral_compass: 'Moral Compass',
    qLabel_inciting_event: 'Inciting Event',
    qLabel_first_plot_point: 'First Plot Point',
    qLabel_midpoint_char: 'The Midpoint',
    qLabel_third_plot_point: 'Third Plot Point',
    qLabel_legitimate_threat: 'Legitimate Threat',
    qLabel_proactive_agency: 'Proactive Agency',
    qLabel_early_victories: 'Early Victories',
    qLabel_distinct_voice: 'Distinct Voice',
    qLabel_the_silhouette: 'The Silhouette',
    qLabel_vocal_anchor: 'Vocal Anchor',
    qLabel_habit_tic: 'Habit / Tic',
    qLabel_entrance_exit: 'Entrance & Exit',
    qLabel_climax: 'The Climax',
    qLabel_new_normal: 'The New Normal',
    qLabel_transformation: 'Transformation',
    qLabel_redeeming_trait: 'Redeeming Trait',
    qLabel_vulnerability: 'Vulnerability',
    qLabel_no_caricatures: 'No Caricatures',
    qLabel_the_right_point: 'The Right Point',
    qLabel_first_impression_vs_reality: 'First Impression vs Reality',
    qLabel_pressure_point: 'Pressure Point',
    qLabel_the_ending: 'The Ending',

    // ── Character question placeholders ──
    qPh_name: 'Character full name',
    qPh_age: 'How old are they?',
    qPh_gender: 'Their gender identity',
    qPh_ethnicity: 'Cultural background',
    qPh_biography: 'Who, What, When, Where, Why — A brief overview of their life',
    qPh_the_lie: 'What false belief holds your character back?',
    qPh_the_lie_neg: 'What false belief will lead to their downfall?',
    qPh_the_truth: 'What truth must they discover?',
    qPh_the_truth_flat: 'What truth does your character already hold?',
    qPh_the_ghost: 'What past trauma created the Lie?',
    qPh_the_ghost_neg: 'What past trauma drives their fall?',
    qPh_the_ghost_flat: 'What experience solidified this truth?',
    qPh_wants: 'What does your character think they want?',
    qPh_wants_neg: 'What does your character desire?',
    qPh_wants_flat: 'What external goal drives them?',
    qPh_needs: 'What do they actually need?',
    qPh_needs_ignored: 'What do they actually need but will ignore?',
    qPh_corruption: 'What temptation will they succumb to?',
    qPh_mission: 'How will they share their truth with the world?',
    qPh_hero_narrative: 'How does the antagonist see themselves as the hero of their own story?',
    qPh_tangible_goal: 'What concrete outcome are they pursuing?',
    qPh_human_roots: 'What human experience or pain drives this character?',
    qPh_internal_logic: 'What is the internal code that justifies their actions?',
    qPh_defined_role: 'What narrative function does this character serve?',
    qPh_plot_impact: 'How do they directly move the story forward?',
    qPh_the_trade: 'What do they give to the protagonist — and what do they take?',
    qPh_normal_world: 'Describe their ordinary life before the journey',
    qPh_persona: 'How do they present themselves to the world?',
    qPh_core_trait: 'What defining characteristic do they display?',
    qPh_characteristic_moment: 'A scene that shows who they are now',
    qPh_direct_conflict: 'How does this character directly oppose the protagonist?',
    qPh_mirror_effect: "How do they reflect the protagonist's shadow self or worst-case future?",
    qPh_targeted_pressure: 'What specific weakness or fear of the protagonist do they exploit?',
    qPh_the_wound: 'What personal wound makes this antagonist who they are?',
    qPh_offscreen_goal: "What is their life like when they're not in the scene?",
    qPh_personal_stakes: "What do they have to lose in the story's conflict?",
    qPh_the_secret: 'What are they hiding — even from the protagonist?',
    qPh_moral_compass: 'What do they believe is right? Where is their line?',
    qPh_inciting_event: 'What disrupts their normal world?',
    qPh_first_plot_point: 'The point of no return',
    qPh_midpoint_char: 'Moment of Truth — everything changes',
    qPh_third_plot_point: 'The "Death" moment — lowest point',
    qPh_legitimate_threat: 'Why is this character a credible danger to the protagonist?',
    qPh_proactive_agency: 'How do they drive the plot — not just react to the protagonist?',
    qPh_early_victories: 'Describe a moment where the antagonist wins or gains ground',
    qPh_distinct_voice: 'What makes their dialogue and presence instantly recognisable?',
    qPh_the_silhouette: 'Describe their look — something visually distinct about them',
    qPh_vocal_anchor: "A phrase, rhythm, or speech pattern that's uniquely theirs",
    qPh_habit_tic: 'A recurring behaviour, gesture, or ritual that reveals character',
    qPh_entrance_exit: 'How do they typically enter and leave a scene?',
    qPh_climax: 'Testing the Truth — final confrontation',
    qPh_new_normal: 'How has their world changed?',
    qPh_transformation: 'How have they changed as a person?',
    qPh_redeeming_trait: 'One genuine quality that makes them human — not likeable, just real',
    qPh_vulnerability: 'What can break through their armour? What do they fear?',
    qPh_no_caricatures: 'How does this character avoid becoming a cartoon villain?',
    qPh_the_right_point: 'What truth, if any, does this antagonist carry that the protagonist (or audience) must reckon with?',
    qPh_first_impression_vs_reality: 'How does your initial read of them differ from who they really are?',
    qPh_pressure_point: 'What situation forces them to reveal their true self?',
    qPh_the_ending: 'Where do they end up? How has the story changed them — even slightly?',

    // ── Scene Builder ──
    scene_builder: 'Scene Builder',
    scene_newScene: 'New Scene',
    scene_editScene: 'Edit Scene',
    scene_addFirst: 'Add First Scene',
    scene_noScenes: 'No scenes yet',
    scene_noScenesDesc: 'Add scenes to break your story beats into shootable moments.',
    scene_noScenesBeat: 'Add scenes for "{{beat}}" to break it into shootable moments.',
    scene_titleLabel: 'Scene Title',
    scene_titleRequired: 'Scene Title *',
    scene_titlePh: 'INT. MARKET — DAY',
    scene_locationLabel: 'Location',
    scene_locationPh: 'INT. NAIROBI MARKET — GOLDEN HOUR',
    scene_plotProjectLabel: 'Plot Project',
    scene_beatLabel: 'Beat',
    scene_standalone: 'Standalone',
    scene_none: 'None',
    scene_bmoc: 'B.M.O.C',
    scene_actionLabel: 'ACTION — What physically happens in this scene?',
    scene_actionPh: 'Describe the core physical action of the scene',
    scene_wantVsNeedLabel: 'WANT vs NEED — What does the character want, and what do they actually need?',
    scene_wantVsNeedPh: 'Surface desire vs. deeper necessity',
    scene_conflictLabel: 'CONFLICT — What force (person, situation, or internal) opposes them?',
    scene_conflictPh: 'The antagonistic force pushing back',
    scene_turnLabel: 'TURN — How does the scene end differently than it began?',
    scene_turnPh: 'The shift in situation, emotion, or knowledge',
    scene_functionLabel: 'Scene Function',
    scene_fnSelectPh: '— Select function —',
    scene_fnIntroduce: 'Introduce',
    scene_fnEscalate: 'Escalate',
    scene_fnReveal: 'Reveal',
    scene_fnConfront: 'Confront',
    scene_fnResolve: 'Resolve',
    scene_polarityShift: 'Polarity Shift',
    scene_polarityStartPh: 'Hope',
    scene_polarityEndPh: 'Despair',
    scene_notes: 'Notes',
    scene_notesPh: 'Production notes, shot ideas, tone references...',
    scene_saveScene: 'Save Scene',
    scene_scenesCount: '{{count}} / {{limit}} scenes',
    scene_action_card: 'Action',
    scene_wantVsNeed_card: 'Want vs Need',
    scene_conflict_card: 'Conflict',
    scene_turn_card: 'Turn',
    scene_function_card: 'Scene Function',
    scene_beatPrefix: 'Beat: ',

    // ── Project Workspace ──
    ws_overallProgress: 'Overall Progress',
    ws_act1: 'Act I',
    ws_act2: 'Act II',
    ws_act3: 'Act III',
    ws_act1Desc: 'Setup',
    ws_act2Desc: 'Rising Action',
    ws_act3Desc: 'Resolution',
    ws_editAct: 'Edit Act {{n}}',
    ws_completedBeats: '{{done}} / {{total}} beats',
    ws_aiLogline: 'AI Logline & Synopsis',
    ws_aiLoglineDesc: 'Generate a one-sentence pitch and 3-act synopsis from your beat sheet.',
    ws_generateAI: 'Generate with AI',
    ws_generating: 'Generating...',
    ws_logline: 'Logline',
    ws_synopsis: 'Synopsis',
    ws_copy: 'Copy',
    ws_copied: 'Copied',
    ws_3LeftThisMonth: '{{n}} of 3 left this month',
    ws_monthlyGenerations: '3 monthly generations',

    // ── Plot Mentor ──
    pm_title: 'Plot Mentor',
    pm_desc: 'AI coaching on your plot structure and story logic',
    pm_forgeGuide: 'Forge Guide',
    pm_forgeGuideDesc: 'Socratic coaching — beat by beat',
    pm_stressTest: 'Stress Test',
    pm_stressTestDesc: 'Challenge plot holes & logic gaps',
    pm_startForgeGuide: 'Start Forge Guide',
    pm_runStressTest: 'Run Stress Test',
    pm_readingPlot: 'Reading your plot data...',
    pm_thinking: 'Thinking...',
    pm_replyPh: 'Reply to your mentor...',
    pm_fullStory: 'Full Story',
    pm_act1: 'Act I',
    pm_act2: 'Act II',
    pm_act3: 'Act III',
    pm_usesLeft: '{{n}} of 3 left this month',
    pm_close: 'Close',

    // ── AI Mentor UI ──
    mentor_replyPh: 'Ask your mentor...',
    mentor_thinking: 'Your mentor is thinking...',
    mentor_proFeature: 'AI Mentor is a Pro feature',
    mentor_upgradeBtn: 'Upgrade to Pro',
    mentor_topicTrauma: 'Trauma',
    mentor_topicLie: 'The Lie',
    mentor_topicTruth: 'The Truth',
    mentor_topicWants: 'Wants',
    mentor_topicNeeds: 'Needs',
    mentor_topicRelationships: 'Relationships',
    mentor_topicConflict: 'Conflict',
    mentor_level_identity: 'Identity',
    mentor_level_internalBlueprint: 'Internal Blueprint',
    mentor_level_worldIntro: 'World & Intro',
    mentor_level_pressureCooker: 'Pressure Cooker',
    mentor_level_finalReckoning: 'Final Reckoning',

    // ── Export ──
    export_title: 'Export Story Bible',
    export_sections: 'Sections',
    export_filename: 'Filename',
    export_download: 'Download PDF',
    export_download_as: 'Download {{format}}',
    export_downloading: 'Generating…',
    export_preview: 'Preview',
    export_noSections: 'Select sections on the left to preview content.',

    // ── Character Level chrome ──
    char_roleDesc: 'Defines how this character is positioned in The Web',
    char_arcTypeDesc: 'Determines Level 1 questions for internal blueprint',
    char_completeContinue: 'Complete & Continue',
    char_completeCharacter: 'Complete Character',
    char_enneagramLabel: 'Enneagram Stress-Tester',
    char_enneagramPh: 'Select Personality Archetype',
    char_upgradeToIndie: 'Upgrade to Indie',
    char_enneagramSyncNote: 'This will sync stress patterns to your Plot Forge "The Breaking Point" beat',
    char_enneagramLockedNote: 'Unlock cross-dashboard personality syncing with the Indie plan or above',

    // ── Enneagram types ──
    enn_type1: 'Type 1: The Perfectionist',
    enn_type2: 'Type 2: The Helper',
    enn_type3: 'Type 3: The Achiever',
    enn_type4: 'Type 4: The Individualist',
    enn_type5: 'Type 5: The Investigator',
    enn_type6: 'Type 6: The Loyalist',
    enn_type7: 'Type 7: The Enthusiast',
    enn_type8: 'Type 8: The Challenger',
    enn_type9: 'Type 9: The Peacemaker',

    // ── Arc Helper content ──
    arcHelper_positive_title: 'Change Arc (Positive)',
    arcHelper_positive_desc: 'Your character overcomes their Lie and embraces the Truth, growing as a person.',
    arcHelper_positive_g0: 'The Lie is a false belief that holds them back',
    arcHelper_positive_g1: 'The Truth is what they must discover to grow',
    arcHelper_positive_g2: 'The Ghost is the past event that created the Lie',
    arcHelper_positive_g3: 'They WANT something external, but NEED internal growth',
    arcHelper_positive_t0: 'Make the Lie feel true to your character',
    arcHelper_positive_t1: 'The Truth should directly contradict the Lie',
    arcHelper_positive_t2: 'Their Want and Need often conflict',
    arcHelper_negative_title: 'Fall Arc (Negative)',
    arcHelper_negative_desc: 'Your character succumbs to their Lie, leading to their downfall or corruption.',
    arcHelper_negative_g0: 'The Lie becomes their justification for bad choices',
    arcHelper_negative_g1: 'The Corruption is the temptation they cannot resist',
    arcHelper_negative_g2: 'The Ghost drives their descent',
    arcHelper_negative_g3: 'They ignore what they truly Need',
    arcHelper_negative_t0: 'Make the corruption feel understandable',
    arcHelper_negative_t1: 'Show what they could have been',
    arcHelper_negative_t2: 'The tragedy is in what they lose',
    arcHelper_flat_title: 'Flat Arc',
    arcHelper_flat_desc: 'Your character already knows the Truth and uses it to change the world around them.',
    arcHelper_flat_g0: 'They possess an unwavering Truth',
    arcHelper_flat_g1: 'The Ghost solidified this Truth',
    arcHelper_flat_g2: 'Their mission is to share this Truth',
    arcHelper_flat_g3: 'External obstacles test their conviction',
    arcHelper_flat_t0: 'The world challenges their Truth',
    arcHelper_flat_t1: 'Other characters may change because of them',
    arcHelper_flat_t2: 'Their conviction should be tested but not broken',

    // ── ProjectWorkspace ──
    ws_setup: 'Setup',
    ws_loglineSynopsis: 'Logline & Synopsis',
    ws_addLogline: 'Add your logline and synopsis',
    ws_buildBeatSheet: 'Build your beat sheet act by act.',
    ws_allCharacters: 'All characters in your library.',
    ws_noCharacters: 'No Characters Yet',
    ws_noCharactersDesc: 'Add characters to populate your story.',
    ws_createFirstChar: 'Create First Character',
    ws_scenesDesc: 'Scenes attached to this plot.',
    ws_noScenes: 'No Scenes Yet',
    ws_noScenesDesc: 'Build scenes for each beat in your plot.',
    ws_openSceneBuilder: 'Open Scene Builder',
    ws_indieFeature: 'Indie Feature',
    ws_webIndieDesc: 'The Character Web is available on the Indie plan and above.',
    ws_webDesc: 'Map relationships and thematic arguments between characters.',
    export_sparkNote: 'Spark — PDF includes watermark',
  }
};

// Swahili translations
const sw = {
  translation: {
    dashboard: 'Dashibodi',
    characterLab: 'Maabara ya Wahusika',
    plotForge: 'Karakana ya Msuko',
    theWeb: 'Wavu',
    worldBuilding: 'Ujenzi wa Ulimwengu',
    logout: 'Ondoka',
    upgrade: 'Boresha hadi Pro',
    settings: 'Mipangilio',
    
    level0Title: 'Ngazi 0: Utambulisho',
    level0Subtitle: 'Msingi',
    level1Title: 'Ngazi 1: Ramani ya Ndani',
    level1Subtitle: 'Roho na Asili',
    level2Title: 'Ngazi 2: Ulimwengu na Utangulizi',
    level2Subtitle: 'Mpangilio',
    level3Title: 'Ngazi 3: Shinikizo',
    level3Subtitle: 'Safari',
    level4Title: 'Ngazi 4: Hukumu ya Mwisho',
    level4Subtitle: 'Utatuzi',
    
    name: 'Jina',
    age: 'Umri',
    gender: 'Jinsia',
    ethnicity: 'Kabila',
    shortBiography: 'Wasifu Mfupi',
    role: 'Jukumu',
    arcType: 'Aina ya Mkondo',
    
    protagonist: 'Mhusika Mkuu',
    antagonist: 'Mpinzani',
    supporting: 'Msaidizi',
    
    changePositive: 'Mabadiliko (Chanya)',
    fallNegative: 'Kuanguka (Hasi)',
    flat: 'Tambarare',
    
    arcHelper: 'Msaidizi wa Mkondo',
    keyElements: 'Vipengele Muhimu',
    proTips: 'Vidokezo vya Wataalamu',
    
    theLie: 'Uongo',
    theTruth: 'Ukweli',
    theGhost: 'Mzimu',
    wants: 'Anataka',
    needs: 'Anahitaji',
    corruption: 'Ufisadi',
    mission: 'Dhamira',
    
    characterRelationshipMatrix: 'Matriki ya Uhusiano',
    addRelationship: 'Ongeza Uhusiano',
    characterA: 'Mhusika A',
    characterB: 'Mhusika B',
    moralDifference: 'Tofauti ya Maadili',
    tacticalDifference: 'Tofauti ya Mbinu',
    centralArgument: 'Hoja Kuu',
    conflictGauge: 'Kipimo cha Mgogoro',
    
    locations: 'Maeneo',
    costumes: 'Mavazi',
    props: 'Vifaa',
    description: 'Maelezo',
    vibe: 'Hali',
    plotSignificance: 'Umuhimu wa Msuko',
    aesthetic: 'Urembo',
    evolution: 'Mageuzi',
    owner: 'Mmiliki',
    symbolicValue: 'Thamani ya Ishara',
    function: 'Kazi',
    
    save: 'Hifadhi',
    saveProgress: 'Hifadhi Maendeleo',
    saveStatus_saving: 'Inahifadhi…',
    saveStatus_dirty: 'Mabadiliko hayajahifadhiwa…',
    saveStatus_notSaving: 'HAIHIFADHI — nakili kazi yako',
    saveStatus_retrying: 'Imeshindwa kuhifadhi — inajaribu tena…',
    saveStatus_saved: 'Imehifadhiwa',
    saveStatus_savedAt: 'Imehifadhiwa {{time}}',
    saveStatus_conflict: 'Mradi huu ulibadilishwa mahali pengine — pakia upya kichupo hiki ili kuendelea',
    milestone_developed: '{{name}} imeendelezwa kikamilifu.',
    char_completeLevel: 'Kamilisha Kiwango {{n}}',
    scene_unassigned: 'Hazijapangwa',
    char_depthDeveloped: '{{levels}} zimeendelezwa',
    autosave_navBlocked: 'Mabadiliko yako bado hayajahifadhiwa — nakili kazi yako, kisha jaribu tena',
    cancel: 'Ghairi',
    delete: 'Futa',
    edit: 'Hariri',
    newCharacter: 'Mhusika Mpya',
    newPlot: 'Msuko Mpya',
    exportPdf: 'Hamisha PDF',
    stressTest: 'Jaribu Msongo',
    aiMentor: 'Mshauri wa AI',
    
    storyAuditor: 'Mkaguzi wa Hadithi',
    runStressTest: 'Fanya Jaribio',
    storyHealthScore: 'Alama ya Afya ya Hadithi',
    issuesFound: 'Matatizo Yaliyopatikana',
    suggestions: 'Mapendekezo',
    strengths: 'Nguvu',
    
    upgradeToPro: 'Boresha hadi Pro',
    monthly: 'Kila Mwezi',
    yearly: 'Kila Mwaka',
    perMonth: '/mwezi',
    perYear: '/mwaka',
    subscribe: 'Jiandikishe',
    
    loading: 'Inapakia...',
    saved: 'Imehifadhiwa',
    error: 'Kuna tatizo',
    proFeature: 'Kipengele cha Pro',

    showForgeGuide: 'Onyesha Mwongozo wa Forge',
    hideForgeGuide: 'Ficha Mwongozo wa Forge',

    forgeGuide_firstFrame: "Hii ndiyo hisia ya kwanza ya ulimwengu wako. Tuonyeshe picha ya maisha ya mhusika wako KABLA ya mabadiliko yote hayajatokea. Inapaswa kuwa kioo cha kuona na kuhisi ambapo mwishowe wataishia — hadhira haitajua bado, lakini wewe unajua. Jiulize: kawaida inaonekana vipi kwa mtu huyu, na wako mbali kiasi gani na mtu wanaohitaji kuwa?",
    forgeGuide_coreTruth: "Mahali fulani katika tendo lako la kwanza, mhusika — mara nyingi si shujaa — anasema kitu ambacho ni mgongo wa kidramatiaji wa hadithi yako yote. Mhusika wako mkuu labda hatakubaliana nacho sasa hivi. Mwishowe, ataelewa kabisa. Ukweli mmoja ni nini ambao hadithi yako inajaribu kuthibitisha?",
    forgeGuide_worldBefore: "Wasilisha mhusika wako mkuu katika mazingira yake. Tunahitaji kuona ulimwengu wake, mahusiano yake, kasoro zake, na haja yake ambayo haijatimizwa. Mambo sita lazima yaanzishwe hapa: mhusika, hatari, aina, sauti, kasoro ambayo shujaa lazima ashinde, na ulimwengu ambao unakaribia kuvurugwa. Chukua wakati wako — hii ndiyo dakika ya mwisho ya amani.",
    forgeGuide_spark: "Kitu kinatokea ambacho mhusika mkuu HAKUSABABISHA na hawezi kupuuza. Huu ndio tukio linalobisha mlango wa ulimwengu wao wa kawaida na kudai jibu. Inapaswa kuhisi ya kushangaza na ya lazima. Si uamuzi — ni ulimwengu unaotengeneza hatua. Ni nini kinamtokea shujaa wako kinachoilazimisha hadithi kuanza?",
    forgeGuide_crossroads: "Shujaa wako anajua Kichocheo kilitokea, lakini hawako tayari kutenda. Hii ndiyo mchezo wa ndani — niende au nibaki? Kadri mjadala huu unavyokuwa mrefu na wa maumivu, ndivyo hadhira inavyowekeza zaidi. Shujaa wako anahitaji msukumo wa mwisho — taarifa, tishio, au dakika ya ujasiri — hatimaye kujitolea. Nini kinawazuia, na nini hatimaye kinawaendesha?",
    forgeGuide_leap: "Mhusika wako mkuu hufanya uchaguzi wa makusudi na hai wa kuingia ulimwengu mpya — kimwili au kwa mfano. Hadithi ya Tendo la Kwanza imekwisha. Hadithi mpya inaanza. Hii inapaswa kuhisi kama hatua ya kutorudi nyuma. Hadhira inapaswa kusikia kwamba hakuna kitu kitakachokuwa sawa tena. Shujaa wako anakisogea wapi, na anaacha nini nyuma?",
    forgeGuide_heartThread: "Hii ndiyo njama ndogo ya kihisia yako — kawaida uhusiano unaobeba mada. Wakati hadithi ya A inahusu kile shujaa ANATAKA, Uzi wa Moyo unahusu kile wanachohitaji. Mara nyingi mshauri, kipenzi, au urafiki usiotarajiwa. Uhusiano huu utamfundisha mhusika wako mkuu Ukweli wa Msingi mwishowe. Ni nani mtu huyu, na wanawakilisha nini ambacho shujaa wako anakosa?",
    forgeGuide_promise: "Hii ndiyo sababu watu walinunua tiketi. Furaha, msisimko, mapenzi, hofu — chochote aina yako inavyoahidi, ipeleke hapa. Mhusika wako mkuu yuko katika ulimwengu mpya na anachunguza. Mambo hayaendi vizuri kabisa, lakini hayavunjiki bado. Hii ndiyo sehemu ya kuvutia zaidi ya filamu yako. Je, unaweka ahadi ambayo aina yako ilifanya kwa hadhira?",
    forgeGuide_turningTide: "Nusu ya hadithi yako, kitu kikubwa kinabadilika. Shujaa wako ana ushindi wa uwongo au kushindwa kwa uwongo — wakati mambo yanaonekana mabaya lakini shujaa anapata nguvu. Hatari zinaongezeka mara mbili hapa. Mhusika wako mkuu lazima aache kujibu na aanze kuendesha hadithi. Nini kinabadilika katikati ya hadithi kinachofanya nusu ya pili kuwa aina tofauti kabisa ya hadithi?",
    forgeGuide_wallsClosing: "Shujaa wako anafuatia lengo lake, lakini sasa kila nguvu — ya ndani na ya nje — inarudisha nyuma. Shaka, maadui, bahati mbaya, usaliti. Mvutano lazima uhisi wa kukosa pumzi. Hadhira lazima kweli kweli washangae kama shujaa wako anaweza kufanikisha. Hii si maumivu ya nasibu — kila kikwazo lazima kifichue kasoro ya kina zaidi ambayo shujaa hajashughulikia bado. Ni nani au nini kinakaribia, na kwa nini kinauma zaidi sasa?",
    forgeGuide_breakingPoint: "Shujaa wako ameshindwa. Si tu lengo la nje — wamepoteza kitu kilichokuwa muhimu zaidi kihisia. Uhusiano, imani, mshauri, sehemu yao wenyewe. Hii ndiyo dakika ya chini zaidi katika hadithi. Inapaswa kuhisi ya mwisho. Hadhira lazima waamini, hata kwa muda mfupi, kwamba hakuna njia ya kurudi. Ni nini kitu kibaya zaidi kinachoweza kumtokea shujaa wako sasa hivi — na kifanye kitokee.",
    forgeGuide_hollow: "Baada ya Hatua ya Kuvunjika, shujaa wako anakaa katika magofu. Hii ni dakika ya utulivu kabla ya dhoruba. Lazima wakabiliwe na uongo waliokuwa wakiishi na kuchagua — kwa ufahamu na maumivu — kukumbatia Ukweli wa Msingi. Hii ndiyo roho ya hadithi yako. Hakuna anayeweza kuwaokoa hapa. Lazima wajiokoe wenyewe. Shujaa wako anatambua nini katika dakika yao ya giza zaidi?",
    forgeGuide_rising: "Shujaa wako anaibuka kutoka Penye Utulivu amebadilika. Akiwa na ukweli, anaandaa mpango wa mwisho. Zana za zamani hazitafanya kazi — lazima watumie wamekuwa NANI, si walikuwa nani. Hadithi ya A na Uzi wa Moyo zinaanza kukusanyika. Vita vya mwisho vinakaribia, na kwa mara ya kwanza, shujaa wako yuko tayari kweli kweli. Mpango ni nini, na unaakisi vipi ukuaji wao?",
    forgeGuide_reckoning: "Shujaa wako hutekeleza mpango katika kilele kinachojaribu kila kitu walichojifunza. Mada inathibitishwa, kasoro inashindwa, na mpinzani — wa ndani au wa nje — anakabiliana uso kwa uso. Hadithi ya kila mhusika mkuu inapaswa kutatuliwa hapa. Ifanye ipate kila dakika ambayo hadhira imewekeza. Je, mwisho wako unahisi wa kushangaza NA wa lazima?",
    forgeGuide_lastFrame: "Picha ya mwisho inayoakisi Fremu ya Kwanza — lakini kila kitu kimebadilika. Tunaona ulimwengu BAADA. Mhusika wako mkuu amebadilika. Ahadi ya Ukweli wa Msingi imetimizwa. Picha hii inapaswa kudumu. Inapaswa kuhisi kama noti ya mwisho ya wimbo unaomalizika vizuri. Ulimwengu wa shujaa wako unaonekana vipi sasa kwamba wamekuwa waliokusudiwa kuwa?",

    // ── Common ──
    saving: 'Inahifadhi...',
    close: 'Funga',
    copy: 'Nakili',
    copied: 'Imenakiliwa',
    previous: 'Iliyotangulia',
    next: 'Inayofuata',
    back: 'Rudi',
    generating: 'Inatengeneza...',

    // ── Dashboard ──
    dash_title: 'Miradi Yangu',
    dash_newProject: 'Mradi Mpya',
    dash_noProjects: 'Bado hakuna mradi — unda wa kwanza hapa chini',
    dash_emptyTitle: 'Bado Hakuna Miradi',
    dash_emptyDesc: 'Unda mradi wako wa kwanza kuanza kujenga hadithi yako.',
    dash_createFirst: 'Unda Mradi Wako wa Kwanza',
    dash_lastEdited: 'Iliraririshwa mwisho {{date}}',
    dash_progress: '{{pct}}% imekamilika',
    dash_projects_one: 'mradi 1',
    dash_projects_other: 'miradi {{count}}',

    // ── Sidebar ──
    sidebar_back: 'Rudi',
    sidebar_backToProjects: 'Rudi kwa Miradi Yangu',
    sidebar_plot: 'Msuko',
    sidebar_chars: 'Wahusika',
    sidebar_scenes: 'Matukio',
    sidebar_script: 'Skripti',
    sidebar_world: 'Ulimwengu',
    sidebar_web: 'Wavu',
    sidebar_bible: 'Kitabu',
    sidebar_mentor: 'Mshauri',
    sidebar_char: 'Mhusika',
    sidebar_locked: 'Inahitaji mpango wa Indie au zaidi',

    // ── Navigation ──
    nav_backToDashboard: 'Dashibodi',
    nav_backToWorkspace: 'Rudi Sehemu ya Kazi',
    nav_backToPlot: 'Rudi kwa Msuko',

    // ── Plot Acts ──
    plotAct1Title: 'Tendo la Kwanza: Kuanza',
    plotAct1Sub: 'Mpangilio',
    plotAct2Title: 'Tendo la Pili: Mapambano',
    plotAct2Sub: 'Hatua Zinazopanda',
    plotAct3Title: 'Tendo la Tatu: Utatuzi',
    plotAct3Sub: 'Kilele na Utatuzi',

    // ── Plot level UI ──
    plot_overallProgress: 'Maendeleo Yote',
    plot_beatOf: 'Hatua {{current}} kati ya {{total}}',
    plot_buildScenes: 'Jenga Matukio',
    plot_complete: 'Kamilisha Msuko',
    plot_nextBeat: 'Hatua Inayofuata',

    // ── Beat placeholders ──
    beatPh_opening_image: 'Picha ya ulimwengu wako wa hadithi kabla mabadiliko hayajaanza',
    beatPh_setup: 'Wasilisha mhusika mkuu, ulimwengu wake, na mahusiano ya awali',
    beatPh_theme_stated: 'Kidokezo cha somo au ukweli ambao mhusika mkuu atajifunza',
    beatPh_catalyst: 'Tukio la kuchochea ambalo linauvuruga ulimwengu wa kawaida',
    beatPh_debate: 'Mhusika mkuu anasita au anahoji kama ajitolee kwa safari',
    beatPh_break_into_two: 'Mhusika mkuu anajitolea na kuingia ulimwengu au hali mpya',
    beatPh_b_story: 'Utangulizi wa hadithi ndogo au uhusiano unaoakisi mada',
    beatPh_fun_and_games: 'Ahadi ya muhtasari — kile ambacho hadhira ililikuja kuona',
    beatPh_midpoint: 'Ushindi au kushindwa kubwa kunachoinua hatarini',
    beatPh_bad_guys_close_in: 'Nguvu za nje na za ndani zinazozidi kukandamiza mhusika mkuu',
    beatPh_all_is_lost: 'Hatua ya chini zaidi — kushindwa kwa uongo au kikwazo kikubwa',
    beatPh_dark_night: 'Mhusika mkuu anashughulikia kushindwa kwake kabla ya kuzaliwa upya',
    beatPh_break_into_three: 'Mhusika mkuu anapata suluhisho au njia mpya',
    beatPh_finale: 'Mapambano ya mwisho ambapo mhusika mkuu anathibitisha mabadiliko yake',
    beatPh_final_image: 'Kioo cha picha ya mwanzo kinachoonyesha kiasi kilichobadilika',
    beatPh_obstacle: 'Mgogoro au changamoto kuu inazidi',
    beatPh_escalation: 'Hatarini zinaongezeka, matatizo yanazidi',

    // ── Character Level titles ──
    charLevel0Title: 'Ngazi 0: Utambulisho',
    charLevel0Sub: 'Msingi',
    charLevel1Title: 'Ngazi 1: Ramani ya Ndani',
    charLevel1Sub: 'Roho na Asili',
    charLevel2Title: 'Ngazi 2: Ulimwengu na Utangulizi',
    charLevel2Sub: 'Mpangilio',
    charLevel3Title: 'Ngazi 3: Shinikizo',
    charLevel3Sub: 'Safari',
    charLevel4Title: 'Ngazi 4: Hukumu ya Mwisho',
    charLevel4Sub: 'Utatuzi',
    charAntag1Title: 'Ngazi 1: Nia na Mantiki ya Ndani',
    charAntag1Sub: 'Sababu Wanayopigana',
    charAntag2Title: 'Ngazi 2: Uhusiano na Mhusika Mkuu',
    charAntag2Sub: 'Upinzani',
    charAntag3Title: 'Ngazi 3: Nguvu na Uwepo',
    charAntag3Sub: 'Tishio na Hiari',
    charAntag4Title: 'Ngazi 4: Utata na Ubinadamu',
    charAntag4Sub: 'Zaidi ya Mbaya',
    charSupp1Title: 'Ngazi 1: Kwa Nini',
    charSupp1Sub: 'Kusudi na Jukumu',
    charSupp2Title: 'Ngazi 2: Ni Nani',
    charSupp2Sub: 'Mhusika na Kina',
    charSupp3Title: 'Ngazi 3: Jinsi Gani',
    charSupp3Sub: 'Sauti na Uwepo',
    charSupp4Title: 'Ngazi 4: Ukuaji',
    charSupp4Sub: 'Mkondo na Athari',

    // ── Character question labels ──
    qLabel_name: 'Jina',
    qLabel_age: 'Umri',
    qLabel_gender: 'Jinsia',
    qLabel_ethnicity: 'Kabila',
    qLabel_biography: 'Wasifu Mfupi',
    qLabel_the_lie: 'Uongo',
    qLabel_the_truth: 'Ukweli',
    qLabel_the_ghost: 'Mzimu (Hiari kwa Filamu Fupi)',
    qLabel_wants: 'Anataka',
    qLabel_needs: 'Anahitaji',
    qLabel_needs_ignored: 'Anahitaji (Anapuuza)',
    qLabel_corruption: 'Ufisadi',
    qLabel_mission: 'Dhamira',
    qLabel_hero_narrative: 'Hadithi Yake ya Ushujaa',
    qLabel_tangible_goal: 'Lengo Halisi',
    qLabel_human_roots: 'Asili ya Kibinadamu',
    qLabel_internal_logic: 'Mantiki ya Ndani',
    qLabel_defined_role: 'Jukumu Lililowekwa',
    qLabel_plot_impact: 'Athari kwa Msuko',
    qLabel_the_trade: 'Biashara',
    qLabel_normal_world: 'Ulimwengu wa Kawaida',
    qLabel_persona: 'Kivuli',
    qLabel_core_trait: 'Sifa Kuu',
    qLabel_characteristic_moment: 'Wakati wa Kipekee',
    qLabel_direct_conflict: 'Mgogoro wa Moja kwa Moja',
    qLabel_mirror_effect: 'Athari ya Kioo',
    qLabel_targeted_pressure: 'Shinikizo Liliolenga',
    qLabel_the_wound: 'Jeraha',
    qLabel_offscreen_goal: 'Lengo Nje ya Tukio',
    qLabel_personal_stakes: 'Hatarini za Kibinafsi',
    qLabel_the_secret: 'Siri',
    qLabel_moral_compass: 'Dira ya Maadili',
    qLabel_inciting_event: 'Tukio la Kuchochea',
    qLabel_first_plot_point: 'Hatua ya Kwanza ya Msuko',
    qLabel_midpoint_char: 'Katikati ya Hadithi',
    qLabel_third_plot_point: 'Hatua ya Tatu ya Msuko',
    qLabel_legitimate_threat: 'Tishio Halisi',
    qLabel_proactive_agency: 'Utendaji wa Hiari',
    qLabel_early_victories: 'Ushindi wa Mapema',
    qLabel_distinct_voice: 'Sauti ya Kipekee',
    qLabel_the_silhouette: 'Kivuli cha Nje',
    qLabel_vocal_anchor: 'Nanga ya Sauti',
    qLabel_habit_tic: 'Tabia / Desturi',
    qLabel_entrance_exit: 'Kuingia na Kutoka',
    qLabel_climax: 'Kilele',
    qLabel_new_normal: 'Kawaida Mpya',
    qLabel_transformation: 'Mabadiliko',
    qLabel_redeeming_trait: 'Sifa ya Kukomboa',
    qLabel_vulnerability: 'Udhaifu',
    qLabel_no_caricatures: 'Sio Mchekesho',
    qLabel_the_right_point: 'Ukweli Unaobebwa',
    qLabel_first_impression_vs_reality: 'Hisia ya Kwanza dhidi ya Ukweli',
    qLabel_pressure_point: 'Hatua ya Shinikizo',
    qLabel_the_ending: 'Mwisho',

    // ── Character question placeholders ──
    qPh_name: 'Jina kamili la mhusika',
    qPh_age: 'Ana miaka mingapi?',
    qPh_gender: 'Utambulisho wao wa kijinsia',
    qPh_ethnicity: 'Asili ya kitamaduni',
    qPh_biography: 'Nani, Nini, Lini, Wapi, Kwa Nini — Muhtasari mfupi wa maisha yao',
    qPh_the_lie: 'Imani gani ya uongo inayomzuia mhusika wako?',
    qPh_the_lie_neg: 'Imani gani ya uongo itakayosababisha anguko lake?',
    qPh_the_truth: 'Ukweli gani lazima agundue?',
    qPh_the_truth_flat: 'Ukweli gani mhusika wako tayari anao?',
    qPh_the_ghost: 'Msiba gani wa zamani uliounda Uongo?',
    qPh_the_ghost_neg: 'Msiba gani wa zamani unaoongoza anguko lake?',
    qPh_the_ghost_flat: 'Uzoefu gani uliimarisha ukweli huu?',
    qPh_wants: 'Mhusika wako anafikiri anataka nini?',
    qPh_wants_neg: 'Mhusika wako anatamani nini?',
    qPh_wants_flat: 'Lengo gani la nje linalomchochea?',
    qPh_needs: 'Wanahitaji nini kweli kweli?',
    qPh_needs_ignored: 'Wanahitaji nini kweli kweli lakini watapuuza?',
    qPh_corruption: 'Kishawishi gani watashindwa kupinga?',
    qPh_mission: 'Watashirikishaje ukweli wao na ulimwengu?',
    qPh_hero_narrative: 'Mpinzani anajionyeshaje kama shujaa wa hadithi yake mwenyewe?',
    qPh_tangible_goal: 'Matokeo gani halisi wanayofuatilia?',
    qPh_human_roots: 'Uzoefu gani wa kibinadamu au maumivu yanayomchochea mhusika huyu?',
    qPh_internal_logic: 'Nini kanuni ya ndani inayohalalisha vitendo vyao?',
    qPh_defined_role: 'Mhusika huyu anatekeleza kazi gani ya hadithi?',
    qPh_plot_impact: 'Wanaendelezaje hadithi moja kwa moja?',
    qPh_the_trade: 'Wanampa nini mhusika mkuu — na wanachukua nini?',
    qPh_normal_world: 'Elezea maisha yao ya kawaida kabla ya safari',
    qPh_persona: 'Wanajionyeshaje kwa ulimwengu?',
    qPh_core_trait: 'Sifa gani inayowatambulisha wanaonyesha?',
    qPh_characteristic_moment: 'Tukio linaloonyesha ni nani wao sasa',
    qPh_direct_conflict: 'Mhusika huyu anapinga mhusika mkuu vipi moja kwa moja?',
    qPh_mirror_effect: "Wanaakisi sehemu gani ya kivuli cha mhusika mkuu au mustakabali mbaya zaidi?",
    qPh_targeted_pressure: 'Udhaifu au hofu gani ya mhusika mkuu wanaitumia?',
    qPh_the_wound: 'Jeraha gani la kibinafsi linaloumba mpinzani huyu?',
    qPh_offscreen_goal: 'Maisha yao yanaonekanaje wanapokuwa nje ya tukio?',
    qPh_personal_stakes: "Wana nini cha kupoteza katika mgogoro wa hadithi?",
    qPh_the_secret: 'Wanaficha nini — hata kutoka kwa mhusika mkuu?',
    qPh_moral_compass: 'Wanaamini nini ni sawa? Mstari wao uko wapi?',
    qPh_inciting_event: 'Kinachofanyia ulimwengu wao wa kawaida?',
    qPh_first_plot_point: 'Hatua ya kutorudi nyuma',
    qPh_midpoint_char: 'Wakati wa Ukweli — kila kitu kinabadilika',
    qPh_third_plot_point: 'Wakati wa "Kifo" — hatua ya chini zaidi',
    qPh_legitimate_threat: 'Kwa nini mhusika huyu ni hatari inayoaminika kwa mhusika mkuu?',
    qPh_proactive_agency: 'Wanaendelezaje msuko — si tu kujibu mhusika mkuu?',
    qPh_early_victories: 'Elezea wakati ambapo mpinzani anashinda au kupata nguvu',
    qPh_distinct_voice: 'Nini kinafanya mazungumzo na uwepo wao kutambuliwa mara moja?',
    qPh_the_silhouette: 'Elezea mwonekano wao — kitu kinachowatofautisha kwa macho',
    qPh_vocal_anchor: 'Msemo, mdundo, au mtindo wa mazungumzo unaowatambulisha',
    qPh_habit_tic: 'Tabia inayorudiwa, ishara, au ibada inayofunua mhusika',
    qPh_entrance_exit: 'Kawaida waingiaje na watokeje katika tukio?',
    qPh_climax: 'Kujaribu Ukweli — mapambano ya mwisho',
    qPh_new_normal: 'Ulimwengu wao umebadilika vipi?',
    qPh_transformation: 'Wamebadilika vipi kama mtu?',
    qPh_redeeming_trait: 'Sifa moja halisi inayowafanya kuwa binadamu — si wa kupenda, tu wa kweli',
    qPh_vulnerability: 'Nini kinaweza kupita kinga yao? Wanaogopa nini?',
    qPh_no_caricatures: 'Mhusika huyu anaepukaje kuwa mwovu wa katuni?',
    qPh_the_right_point: 'Ukweli gani, ikiwa upo, ambao mpinzani huyu anabeba ambao mhusika mkuu (au hadhira) lazima akabiliane nao?',
    qPh_first_impression_vs_reality: 'Hisia yako ya kwanza inatofautianaje na wao kweli kweli?',
    qPh_pressure_point: 'Hali gani inayowalazimisha kufunua nafsi yao ya kweli?',
    qPh_the_ending: 'Wanakwenda wapi? Hadithi imewabadilisha vipi — hata kidogo?',

    // ── Scene Builder ──
    scene_builder: 'Mjenzi wa Matukio',
    scene_newScene: 'Tukio Jipya',
    scene_editScene: 'Hariri Tukio',
    scene_addFirst: 'Ongeza Tukio la Kwanza',
    scene_noScenes: 'Bado hakuna matukio',
    scene_noScenesDesc: 'Ongeza matukio kugawanya hatua za hadithi yako katika nyakati za kupigwa picha.',
    scene_noScenesBeat: 'Ongeza matukio kwa "{{beat}}" kugawanya katika nyakati za kupigwa picha.',
    scene_titleLabel: 'Kichwa cha Tukio',
    scene_titleRequired: 'Kichwa cha Tukio *',
    scene_titlePh: 'NDANI. SOKO — MCHANA',
    scene_locationLabel: 'Mahali',
    scene_locationPh: 'NDANI. SOKO LA NAIROBI — WAKATI WA DHAHABU',
    scene_plotProjectLabel: 'Mradi wa Msuko',
    scene_beatLabel: 'Hatua',
    scene_standalone: 'Peke Yake',
    scene_none: 'Hakuna',
    scene_bmoc: 'B.M.O.C',
    scene_actionLabel: 'TENDO — Kinachofanyika kimwili katika tukio hili?',
    scene_actionPh: 'Elezea tendo kuu la kimwili la tukio',
    scene_wantVsNeedLabel: 'KUTAKA dhidi ya KUHITAJI — Mhusika anataka nini, na anahitaji nini kweli?',
    scene_wantVsNeedPh: 'Haja ya nje dhidi ya kina cha ndani',
    scene_conflictLabel: 'MGOGORO — Nguvu gani (mtu, hali, au ya ndani) inayowapinga?',
    scene_conflictPh: 'Nguvu inayopinga inayosukuma nyuma',
    scene_turnLabel: 'MWELEKEO — Tukio linamaliziaje tofauti na lilivyoanza?',
    scene_turnPh: 'Mabadiliko katika hali, hisia, au maarifa',
    scene_functionLabel: 'Kazi ya Tukio',
    scene_fnSelectPh: '— Chagua kazi —',
    scene_fnIntroduce: 'Utangulizi',
    scene_fnEscalate: 'Kupanda',
    scene_fnReveal: 'Kufunua',
    scene_fnConfront: 'Kukabiliana',
    scene_fnResolve: 'Kutatua',
    scene_polarityShift: 'Mabadiliko ya Polarity',
    scene_polarityStartPh: 'Matumaini',
    scene_polarityEndPh: 'Kukata Tamaa',
    scene_notes: 'Maelezo',
    scene_notesPh: 'Maelezo ya uzalishaji, mawazo ya picha, marejeo ya sauti...',
    scene_saveScene: 'Hifadhi Tukio',
    scene_scenesCount: '{{count}} / {{limit}} matukio',
    scene_action_card: 'Tendo',
    scene_wantVsNeed_card: 'Kutaka dhidi ya Kuhitaji',
    scene_conflict_card: 'Mgogoro',
    scene_turn_card: 'Mwelekeo',
    scene_function_card: 'Kazi ya Tukio',
    scene_beatPrefix: 'Hatua: ',

    // ── Project Workspace ──
    ws_overallProgress: 'Maendeleo Yote',
    ws_act1: 'Tendo la 1',
    ws_act2: 'Tendo la 2',
    ws_act3: 'Tendo la 3',
    ws_act1Desc: 'Mpangilio',
    ws_act2Desc: 'Hatua Zinazopanda',
    ws_act3Desc: 'Utatuzi',
    ws_editAct: 'Hariri Tendo la {{n}}',
    ws_completedBeats: '{{done}} / {{total}} hatua',
    ws_aiLogline: 'AI Logline & Muhtasari',
    ws_aiLoglineDesc: 'Tengeneza sentensi moja ya pitchi na muhtasari wa matendo matatu kutoka kwa ratiba ya hatua.',
    ws_generateAI: 'Tengeneza kwa AI',
    ws_generating: 'Inatengeneza...',
    ws_logline: 'Logline',
    ws_synopsis: 'Muhtasari',
    ws_copy: 'Nakili',
    ws_copied: 'Imenakiliwa',
    ws_3LeftThisMonth: '{{n}} kati ya 3 zimesalia mwezi huu',
    ws_monthlyGenerations: 'Uzalishaji 3 kwa mwezi',

    // ── Plot Mentor ──
    pm_title: 'Mshauri wa Msuko',
    pm_desc: 'Mafunzo ya AI kuhusu muundo wa msuko wako na mantiki ya hadithi',
    pm_forgeGuide: 'Mwongozo wa Forge',
    pm_forgeGuideDesc: 'Mafunzo ya Kisokrasia — hatua kwa hatua',
    pm_stressTest: 'Jaribio la Msongo',
    pm_stressTestDesc: 'Changamoto kwa mapungufu na mianya ya mantiki',
    pm_startForgeGuide: 'Anza Mwongozo wa Forge',
    pm_runStressTest: 'Fanya Jaribio la Msongo',
    pm_readingPlot: 'Inasoma data ya msuko wako...',
    pm_thinking: 'Inafikiria...',
    pm_replyPh: 'Jibu mshauri wako...',
    pm_fullStory: 'Hadithi Yote',
    pm_act1: 'Tendo la 1',
    pm_act2: 'Tendo la 2',
    pm_act3: 'Tendo la 3',
    pm_usesLeft: '{{n}} kati ya 3 zimesalia mwezi huu',
    pm_close: 'Funga',

    // ── AI Mentor UI ──
    mentor_replyPh: 'Uliza mshauri wako...',
    mentor_thinking: 'Mshauri wako anafikiria...',
    mentor_proFeature: 'Mshauri wa AI ni kipengele cha Pro',
    mentor_upgradeBtn: 'Boresha hadi Pro',
    mentor_topicTrauma: 'Msiba',
    mentor_topicLie: 'Uongo',
    mentor_topicTruth: 'Ukweli',
    mentor_topicWants: 'Anataka',
    mentor_topicNeeds: 'Anahitaji',
    mentor_topicRelationships: 'Mahusiano',
    mentor_topicConflict: 'Mgogoro',
    mentor_level_identity: 'Utambulisho',
    mentor_level_internalBlueprint: 'Ramani ya Ndani',
    mentor_level_worldIntro: 'Ulimwengu & Utangulizi',
    mentor_level_pressureCooker: 'Shinikizo',
    mentor_level_finalReckoning: 'Hukumu ya Mwisho',

    // ── Export ──
    export_title: 'Hamisha Kitabu cha Hadithi',
    export_sections: 'Sehemu',
    export_filename: 'Jina la Faili',
    export_download: 'Pakua PDF',
    export_download_as: 'Pakua {{format}}',
    export_downloading: 'Inatengeneza…',
    export_preview: 'Hakiki',
    export_noSections: 'Chagua sehemu upande wa kushoto kuona maudhui.',

    // ── Character Level chrome ──
    char_roleDesc: 'Inabainisha nafasi ya mhusika huyu katika Wavu',
    char_arcTypeDesc: 'Inabainisha maswali ya Ngazi 1 ya ramani ya ndani',
    char_completeContinue: 'Kamilisha & Endelea',
    char_completeCharacter: 'Kamilisha Mhusika',
    char_enneagramLabel: 'Jaribio la Msongo la Enneagram',
    char_enneagramPh: 'Chagua Aina ya Utu',
    char_upgradeToIndie: 'Boresha hadi Indie',
    char_enneagramSyncNote: 'Hii itasawazisha mifumo ya msongo na hatua yako ya "Hatua ya Kuvunjika" ya Plot Forge',
    char_enneagramLockedNote: 'Fungua usawazishaji wa utu baina ya dashibodi na mpango wa Indie au zaidi',

    // ── Enneagram types ──
    enn_type1: 'Aina 1: Mkamilifu',
    enn_type2: 'Aina 2: Msaidizi',
    enn_type3: 'Aina 3: Mfanikio',
    enn_type4: 'Aina 4: Mtu Binafsi',
    enn_type5: 'Aina 5: Mpelelezi',
    enn_type6: 'Aina 6: Mwaminifu',
    enn_type7: 'Aina 7: Msisimko',
    enn_type8: 'Aina 8: Mpiganaji',
    enn_type9: 'Aina 9: Mtu wa Amani',

    // ── Arc Helper content (SW — falls back to EN if keys absent) ──
    arcHelper_positive_title: 'Mkondo wa Mabadiliko (Chanya)',
    arcHelper_positive_desc: 'Mhusika wako anashinda Uongo wake na kukubali Ukweli, akikua kama mtu.',
    arcHelper_positive_g0: 'Uongo ni imani ya uongo inayomzuia',
    arcHelper_positive_g1: 'Ukweli ndio wanachopaswa kugundua kukua',
    arcHelper_positive_g2: 'Mzimu ni tukio la zamani lililounda Uongo',
    arcHelper_positive_g3: 'WANATAKA kitu cha nje, lakini WANAHITAJI ukuaji wa ndani',
    arcHelper_positive_t0: 'Fanya Uongo uhisi wa kweli kwa mhusika wako',
    arcHelper_positive_t1: 'Ukweli unapaswa kupinga moja kwa moja Uongo',
    arcHelper_positive_t2: 'Kutaka kwao na Kuhitaji kwao mara nyingi vinakinzana',
    arcHelper_negative_title: 'Mkondo wa Kuanguka (Hasi)',
    arcHelper_negative_desc: 'Mhusika wako anashindwa na Uongo wake, na kusababisha kuanguka au ufisadi.',
    arcHelper_negative_g0: 'Uongo unakuwa uhalali wao wa uchaguzi mbaya',
    arcHelper_negative_g1: 'Ufisadi ni kishawishi ambacho hawawezi kupinga',
    arcHelper_negative_g2: 'Mzimu unasukuma kushuka kwao',
    arcHelper_negative_g3: 'Wanapuuza kile wanachohitaji kweli',
    arcHelper_negative_t0: 'Fanya ufisadi uhisi wa kueleweka',
    arcHelper_negative_t1: 'Onyesha wangeweza kuwa nani',
    arcHelper_negative_t2: 'Msiba uko katika wanachopoteza',
    arcHelper_flat_title: 'Mkondo Tambarare',
    arcHelper_flat_desc: 'Mhusika wako tayari anajua Ukweli na unatumia kubadilisha ulimwengu unaomzunguka.',
    arcHelper_flat_g0: 'Wanamiliki Ukweli usioyumba',
    arcHelper_flat_g1: 'Mzimu ulioimarisha Ukweli huu',
    arcHelper_flat_g2: 'Dhamira yao ni kushiriki Ukweli huu',
    arcHelper_flat_g3: 'Vikwazo vya nje vinajaribu imani yao',
    arcHelper_flat_t0: 'Ulimwengu unachallenge Ukweli wao',
    arcHelper_flat_t1: 'Wahusika wengine wanaweza kubadilika kwa sababu yao',
    arcHelper_flat_t2: 'Imani yao inapaswa kujaribiwa lakini isikatike',

    // ── ProjectWorkspace ──
    ws_setup: 'Mpangilio',
    ws_loglineSynopsis: 'Logline & Muhtasari',
    ws_addLogline: 'Ongeza logline na muhtasari wako',
    ws_buildBeatSheet: 'Jenga ratiba ya hatua zako tendo kwa tendo.',
    ws_allCharacters: 'Wahusika wote katika maktaba yako.',
    ws_noCharacters: 'Bado Hakuna Wahusika',
    ws_noCharactersDesc: 'Ongeza wahusika kujaza hadithi yako.',
    ws_createFirstChar: 'Unda Mhusika wa Kwanza',
    ws_scenesDesc: 'Matukio yaliyounganishwa na msuko huu.',
    ws_noScenes: 'Bado Hakuna Matukio',
    ws_noScenesDesc: 'Jenga matukio kwa kila hatua katika msuko wako.',
    ws_openSceneBuilder: 'Fungua Mjenzi wa Matukio',
    ws_indieFeature: 'Kipengele cha Indie',
    ws_webIndieDesc: 'Wavu wa Wahusika unapatikana kwa mpango wa Indie na zaidi.',
    ws_webDesc: 'Onyesha mahusiano na hoja za kidramatiaji kati ya wahusika.',
    export_sparkNote: 'Spark — PDF ina alama ya maji',
  }
};

// Spanish translations
const es = {
  translation: {
    export_download_as: 'Descargar {{format}}',
    export_downloading: 'Generando…',
    dashboard: 'Panel',
    characterLab: 'Laboratorio de Personajes',
    plotForge: 'Forja de Trama',
    theWeb: 'La Red',
    worldBuilding: 'Construcción del Mundo',
    logout: 'Cerrar Sesión',
    upgrade: 'Actualizar a Pro',
    settings: 'Configuración',
    
    level0Title: 'Nivel 0: Identidad',
    level0Subtitle: 'La Base',
    level1Title: 'Nivel 1: El Plano Interno',
    level1Subtitle: 'Alma y Origen',
    level2Title: 'Nivel 2: El Mundo e Introducción',
    level2Subtitle: 'Configuración',
    level3Title: 'Nivel 3: La Olla a Presión',
    level3Subtitle: 'El Viaje',
    level4Title: 'Nivel 4: El Ajuste Final',
    level4Subtitle: 'Resolución',
    
    name: 'Nombre',
    age: 'Edad',
    gender: 'Género',
    ethnicity: 'Etnia',
    shortBiography: 'Biografía Corta',
    role: 'Rol',
    arcType: 'Tipo de Arco',
    
    protagonist: 'Protagonista',
    antagonist: 'Antagonista',
    supporting: 'Secundario',
    
    changePositive: 'Cambio (Positivo)',
    fallNegative: 'Caída (Negativo)',
    flat: 'Plano',
    
    arcHelper: 'Ayudante de Arco',
    keyElements: 'Elementos Clave',
    proTips: 'Consejos Pro',
    
    theLie: 'La Mentira',
    theTruth: 'La Verdad',
    theGhost: 'El Fantasma',
    wants: 'Deseos',
    needs: 'Necesidades',
    corruption: 'La Corrupción',
    mission: 'Misión',
    
    characterRelationshipMatrix: 'Matriz de Relaciones',
    addRelationship: 'Agregar Relación',
    characterA: 'Personaje A',
    characterB: 'Personaje B',
    moralDifference: 'Diferencia Moral',
    tacticalDifference: 'Diferencia Táctica',
    centralArgument: 'Argumento Central',
    conflictGauge: 'Medidor de Conflicto',
    
    locations: 'Ubicaciones',
    costumes: 'Vestuario',
    props: 'Utilería',
    description: 'Descripción',
    vibe: 'Ambiente',
    plotSignificance: 'Significado en la Trama',
    aesthetic: 'Estética',
    evolution: 'Evolución',
    owner: 'Propietario',
    symbolicValue: 'Valor Simbólico',
    function: 'Función',
    
    save: 'Guardar',
    saveProgress: 'Guardar Progreso',
    saveStatus_saving: 'Guardando…',
    saveStatus_dirty: 'Cambios sin guardar…',
    saveStatus_notSaving: 'NO SE GUARDA — copia tu trabajo',
    saveStatus_retrying: 'Error al guardar — reintentando…',
    saveStatus_saved: 'Guardado',
    saveStatus_savedAt: 'Guardado {{time}}',
    saveStatus_conflict: 'Este proyecto se modificó en otro lugar — recarga esta pestaña para continuar',
    milestone_developed: '{{name}} está completamente desarrollado.',
    char_completeLevel: 'Completar Nivel {{n}}',
    scene_unassigned: 'Sin asignar',
    char_depthDeveloped: '{{levels}} desarrollados',
    autosave_navBlocked: 'Tus cambios aún no se han guardado — copia tu trabajo y vuelve a intentarlo',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    newCharacter: 'Nuevo Personaje',
    newPlot: 'Nueva Trama',
    exportPdf: 'Exportar PDF',
    stressTest: 'Prueba de Estrés',
    aiMentor: 'Mentor IA',
    
    storyAuditor: 'Auditor de Historia',
    runStressTest: 'Ejecutar Prueba',
    storyHealthScore: 'Puntuación de Salud',
    issuesFound: 'Problemas Encontrados',
    suggestions: 'Sugerencias',
    strengths: 'Fortalezas',
    
    upgradeToPro: 'Actualizar a Pro',
    monthly: 'Mensual',
    yearly: 'Anual',
    perMonth: '/mes',
    perYear: '/año',
    subscribe: 'Suscribirse',
    
    loading: 'Cargando...',
    saved: 'Guardado',
    error: 'Algo salió mal',
    proFeature: 'Función Pro',

    showForgeGuide: 'Mostrar Guía de Forge',
    hideForgeGuide: 'Ocultar Guía de Forge',

    forgeGuide_firstFrame: "Esta es la primera impresión de tu mundo. Muéstranos una instantánea de la vida de tu protagonista ANTES de que todo cambie. Debe ser un espejo visual y emocional de dónde termina: el público no lo sabrá aún, pero tú sí. Pregúntate: ¿cómo es la normalidad para esta persona y cuán lejos está de quien necesita convertirse?",
    forgeGuide_coreTruth: "En algún lugar de tu primer acto, un personaje — a menudo no el héroe — dice algo que es la columna vertebral temática de toda tu historia. Tu protagonista probablemente no estará de acuerdo ahora. Al final, lo entenderá completamente. ¿Cuál es la única verdad que tu historia intenta demostrar?",
    forgeGuide_worldBefore: "Presenta a tu protagonista en su elemento. Necesitamos ver su mundo, sus relaciones, sus defectos y su necesidad insatisfecha. Seis cosas deben establecerse aquí: personaje, apuestas, género, tono, el defecto que el héroe debe superar y el mundo que está a punto de ser perturbado. Tómate tu tiempo: este es el último momento de paz.",
    forgeGuide_spark: "Algo sucede que el protagonista NO causó y no puede ignorar. Es el evento que llama a la puerta de su mundo ordinario y exige una respuesta. Debe sentirse sorprendente e inevitable. No es una decisión: es el universo haciendo un movimiento. ¿Qué le ocurre a tu héroe que obliga a que la historia comience?",
    forgeGuide_crossroads: "Tu héroe sabe que la Chispa ocurrió, pero no está listo para actuar. Es el combate interno: ¿voy o me quedo? Cuanto más larga y dolorosa sea esta lucha, más invertirá el público. Tu héroe necesita un impulso final — información, una amenaza, un momento de valentía — para comprometerse. ¿Qué lo detiene y qué finalmente lo mueve?",
    forgeGuide_leap: "Tu protagonista hace una elección activa y consciente de entrar en un nuevo mundo — literal o metafóricamente. La historia del Acto I ha terminado. Comienza una nueva historia. Esto debe sentirse como un punto sin retorno. El público debe percibir que nada volverá a ser igual. ¿Dónde cruza tu héroe el umbral y qué deja atrás?",
    forgeGuide_heartThread: "Esta es tu subtrama emocional — generalmente una relación que lleva el tema. Mientras la historia A trata de lo que el héroe QUIERE, el Hilo del Corazón trata de lo que NECESITA. A menudo un mentor, interés romántico o amistad inesperada. Esta relación enseñará a tu protagonista la Verdad Central al final. ¿Quién es esta persona y qué representa lo que le falta al héroe?",
    forgeGuide_promise: "Esta es la razón por la que la gente compró la entrada. La diversión, los sustos, el romance: lo que sea que prometa tu género, entrégalo aquí. Tu protagonista está en el nuevo mundo explorándolo. Las cosas no van perfectamente, pero tampoco se derrumban todavía. Esta es la sección más entretenida de tu película. ¿Estás cumpliendo la promesa que tu género hizo al público?",
    forgeGuide_turningTide: "A mitad de tu historia, algo cambia significativamente. Tu héroe tiene una victoria falsa o una derrota falsa — donde las cosas parecen terribles pero el héroe encuentra su lucha. Las apuestas se duplican aquí. El protagonista debe dejar de reaccionar y empezar a dirigir la historia. ¿Qué cambia en el punto medio que hace que la segunda mitad sea un tipo de historia completamente diferente?",
    forgeGuide_wallsClosing: "Tu héroe persigue su objetivo, pero ahora cada fuerza — interna y externa — empuja hacia atrás. Duda, enemigos, mala suerte, traición. La tensión debe sentirse sofocante. El público debe genuinamente preguntarse si tu héroe puede lograrlo. No es una dificultad aleatoria: cada obstáculo debe exponer un defecto más profundo que el héroe aún no ha enfrentado. ¿Quién o qué se acerca, y por qué duele más ahora?",
    forgeGuide_breakingPoint: "Tu héroe ha perdido. No solo el objetivo externo: han perdido lo que más importaba emocionalmente. Una relación, una creencia, un mentor, una parte de sí mismos. Este es el momento más bajo en la historia. Debe sentirse definitivo. El público debe creer, aunque sea por un momento, que no hay camino de regreso. ¿Qué es lo peor que podría pasarle a tu héroe ahora? Hazlo suceder.",
    forgeGuide_hollow: "Después del Punto de Quiebre, tu héroe se sienta entre los escombros. Este es un momento de quietud antes de la tormenta. Deben confrontar la mentira que han vivido y elegir — consciente y dolorosamente — abrazar la Verdad Central. Este es el alma de tu historia. Nadie puede salvarlos aquí. Deben salvarse a sí mismos. ¿Qué se da cuenta tu héroe en su momento más oscuro y silencioso?",
    forgeGuide_rising: "Tu héroe emerge transformado. Armado con la verdad, formula un plan final. Las herramientas viejas no funcionarán: deben usar quiénes se han CONVERTIDO, no quiénes eran. La historia A y el Hilo del Corazón comienzan a converger. La batalla final se acerca y, por primera vez, tu héroe está verdaderamente listo. ¿Cuál es el plan y cómo refleja su crecimiento?",
    forgeGuide_reckoning: "Tu héroe ejecuta el plan en un clímax que pone a prueba todo lo que ha aprendido. El tema se prueba, el defecto se conquista y el antagonista — interno o externo — se enfrenta de frente. La historia de cada personaje principal debe resolverse aquí. Hazlo merecer cada minuto que el público ha invertido. ¿Tu final se siente sorprendente E inevitable?",
    forgeGuide_lastFrame: "Una imagen final que refleja el Primer Fotograma, pero todo ha cambiado. Vemos el mundo DESPUÉS. Tu protagonista se ha transformado. La promesa de la Verdad Central se ha cumplido. Esta imagen debe perdurar. Debe sentirse como la última nota de una canción que se resuelve perfectamente. ¿Cómo se ve el mundo de tu héroe ahora que se ha convertido en quien siempre estuvo destinado a ser?",
  }
};

// French translations
const fr = {
  translation: {
    dashboard: 'Tableau de Bord',
    characterLab: 'Laboratoire de Personnages',
    plotForge: 'Forge d\'Intrigue',
    theWeb: 'Le Réseau',
    worldBuilding: 'Construction du Monde',
    logout: 'Déconnexion',
    upgrade: 'Passer à Pro',
    settings: 'Paramètres',
    
    level0Title: 'Niveau 0: Identité',
    level0Subtitle: 'La Fondation',
    level1Title: 'Niveau 1: Le Plan Interne',
    level1Subtitle: 'Âme et Origine',
    level2Title: 'Niveau 2: Le Monde et Introduction',
    level2Subtitle: 'Configuration',
    level3Title: 'Niveau 3: La Cocotte-Minute',
    level3Subtitle: 'Le Voyage',
    level4Title: 'Niveau 4: Le Règlement Final',
    level4Subtitle: 'Résolution',
    
    name: 'Nom',
    age: 'Âge',
    gender: 'Genre',
    ethnicity: 'Ethnie',
    shortBiography: 'Biographie Courte',
    role: 'Rôle',
    arcType: 'Type d\'Arc',
    
    protagonist: 'Protagoniste',
    antagonist: 'Antagoniste',
    supporting: 'Secondaire',
    
    changePositive: 'Changement (Positif)',
    fallNegative: 'Chute (Négatif)',
    flat: 'Plat',
    
    arcHelper: 'Assistant d\'Arc',
    keyElements: 'Éléments Clés',
    proTips: 'Conseils Pro',
    
    theLie: 'Le Mensonge',
    theTruth: 'La Vérité',
    theGhost: 'Le Fantôme',
    wants: 'Désirs',
    needs: 'Besoins',
    corruption: 'La Corruption',
    mission: 'Mission',
    
    characterRelationshipMatrix: 'Matrice de Relations',
    addRelationship: 'Ajouter Relation',
    characterA: 'Personnage A',
    characterB: 'Personnage B',
    moralDifference: 'Différence Morale',
    tacticalDifference: 'Différence Tactique',
    centralArgument: 'Argument Central',
    conflictGauge: 'Jauge de Conflit',
    
    locations: 'Lieux',
    costumes: 'Costumes',
    props: 'Accessoires',
    description: 'Description',
    vibe: 'Ambiance',
    plotSignificance: 'Importance dans l\'Intrigue',
    aesthetic: 'Esthétique',
    evolution: 'Évolution',
    owner: 'Propriétaire',
    symbolicValue: 'Valeur Symbolique',
    function: 'Fonction',
    
    save: 'Enregistrer',
    saveProgress: 'Sauvegarder',
    saveStatus_saving: 'Enregistrement…',
    saveStatus_dirty: 'Modifications non enregistrées…',
    saveStatus_notSaving: 'PAS ENREGISTRÉ — copiez votre travail',
    saveStatus_retrying: "Échec de l'enregistrement — nouvel essai…",
    saveStatus_saved: 'Enregistré',
    saveStatus_savedAt: 'Enregistré {{time}}',
    saveStatus_conflict: 'Ce projet a été modifié ailleurs — rechargez cet onglet pour continuer',
    milestone_developed: '{{name}} est entièrement développé.',
    char_completeLevel: 'Terminer le Niveau {{n}}',
    scene_unassigned: 'Non attribué',
    char_depthDeveloped: '{{levels}} développés',
    autosave_navBlocked: "Vos modifications ne sont pas encore enregistrées — copiez votre travail, puis réessayez",
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    newCharacter: 'Nouveau Personnage',
    newPlot: 'Nouvelle Intrigue',
    exportPdf: 'Exporter PDF',
    stressTest: 'Test de Stress',
    aiMentor: 'Mentor IA',
    
    storyAuditor: 'Auditeur d\'Histoire',
    runStressTest: 'Lancer le Test',
    storyHealthScore: 'Score de Santé',
    issuesFound: 'Problèmes Trouvés',
    suggestions: 'Suggestions',
    strengths: 'Forces',
    
    upgradeToPro: 'Passer à Pro',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    perMonth: '/mois',
    perYear: '/an',
    subscribe: 'S\'abonner',
    
    loading: 'Chargement...',
    saved: 'Enregistré',
    error: 'Une erreur est survenue',
    proFeature: 'Fonction Pro',

    showForgeGuide: 'Afficher le Guide Forge',
    hideForgeGuide: 'Masquer le Guide Forge',

    forgeGuide_firstFrame: "C'est la toute première impression de votre monde. Montrez-nous un instantané de la vie de votre protagoniste AVANT que tout change. Ce doit être un miroir visuel et émotionnel de l'endroit où il finira — le public ne le saura pas encore, mais vous, oui. Demandez-vous : à quoi ressemble la normalité pour cette personne, et à quelle distance est-elle de qui elle doit devenir ?",
    forgeGuide_coreTruth: "Quelque part dans votre premier acte, un personnage — souvent pas le héros — dit quelque chose qui constitue la colonne vertébrale thématique de toute votre histoire. Votre protagoniste sera probablement en désaccord avec cela maintenant. À la fin, il le comprendra complètement. Quelle est la seule vérité que votre histoire essaie de prouver ?",
    forgeGuide_worldBefore: "Présentez votre protagoniste dans son élément. Nous devons voir son monde, ses relations, ses défauts et son besoin inassouvi. Six choses doivent être établies ici : le personnage, les enjeux, le genre, le ton, le défaut que le héros doit surmonter et le monde qui va être perturbé. Prenez votre temps — c'est le dernier moment de paix.",
    forgeGuide_spark: "Quelque chose arrive que le protagoniste n'a PAS causé et ne peut ignorer. C'est l'événement qui frappe à la porte de son monde ordinaire et exige une réponse. Il doit sembler à la fois surprenant et inévitable. Ce n'est pas une décision — c'est l'univers qui fait un mouvement. Qu'arrive-t-il à votre héros qui force le début de l'histoire ?",
    forgeGuide_crossroads: "Votre héros sait que la Étincelle s'est produite, mais n'est pas prêt à agir. C'est le combat intérieur : dois-je y aller ou rester ? Plus ce débat est long et douloureux, plus le public s'investit. Votre héros a besoin d'une poussée finale — une information cruciale, une menace ou un moment de courage — pour finalement s'engager. Qu'est-ce qui l'arrête et qu'est-ce qui finalement le fait bouger ?",
    forgeGuide_leap: "Votre protagoniste fait un choix actif et conscient d'entrer dans un nouveau monde — littéralement ou métaphoriquement. L'histoire de l'Acte I est terminée. Une nouvelle histoire commence. Cela doit sembler être un point de non-retour. Le public doit sentir que rien ne sera jamais pareil. Où votre héros franchit-il le seuil et que laisse-t-il derrière lui ?",
    forgeGuide_heartThread: "C'est votre sous-intrigue émotionnelle — généralement une relation qui porte le thème. Alors que l'histoire A concerne ce que le héros VEUT, le Fil du Cœur concerne ce dont il a BESOIN. Souvent un mentor, un intérêt romantique ou une amitié inattendue. Cette relation apprendra à votre protagoniste la Vérité Centrale à la fin. Qui est cette personne et que représente-t-elle que votre héros manque ?",
    forgeGuide_promise: "C'est la raison pour laquelle les gens ont acheté leur billet. Les frissons, le suspense, la romance : quoi que promette votre genre, livrez-le ici. Votre protagoniste est dans le nouveau monde et l'explore. Les choses ne se passent pas parfaitement, mais elles ne s'effondrent pas encore. C'est la section la plus divertissante de votre film. Tenez-vous la promesse que votre genre a faite au public ?",
    forgeGuide_turningTide: "À mi-chemin de votre histoire, quelque chose change considérablement. Votre héros a une fausse victoire ou une fausse défaite — où les choses semblent terribles mais le héros trouve sa combativité. Les enjeux doublent ici. Votre protagoniste doit arrêter de réagir et commencer à diriger l'histoire. Qu'est-ce qui change au milieu du film qui fait de la deuxième moitié une histoire complètement différente ?",
    forgeGuide_wallsClosing: "Votre héros poursuit son objectif, mais maintenant chaque force — interne et externe — résiste. Doute, ennemis, malchance, trahison. La tension doit sembler suffocante. Le public doit vraiment se demander si votre héros peut y arriver. Ce ne sont pas des difficultés aléatoires — chaque obstacle doit exposer un défaut plus profond que le héros n'a pas encore affronté. Qui ou quoi se rapproche, et pourquoi est-ce particulièrement douloureux maintenant ?",
    forgeGuide_breakingPoint: "Votre héros a perdu. Pas seulement l'objectif externe — il a perdu ce qui comptait le plus émotionnellement. Une relation, une croyance, un mentor, une partie de lui-même. C'est le moment le plus bas de l'histoire. Cela doit sembler définitif. Le public doit croire, même un instant, qu'il n'y a pas de retour possible. Quelle est la pire chose qui pourrait arriver à votre héros maintenant — et faites-la arriver.",
    forgeGuide_hollow: "Après le Point de Rupture, votre héros est assis dans les décombres. C'est un moment de calme avant la tempête. Il doit confronter le mensonge qu'il a vécu et choisir — consciemment et douloureusement — d'embrasser la Vérité Centrale. C'est l'âme de votre histoire. Personne ne peut le sauver ici. Il doit se sauver lui-même. Que réalise votre héros dans son moment le plus sombre ?",
    forgeGuide_rising: "Votre héros émerge transformé. Armé de la vérité, il formule un plan final. Les anciens outils ne fonctionneront pas — il doit utiliser qui il est DEVENU, pas qui il était. L'histoire A et le Fil du Cœur commencent à converger. La bataille finale approche et, pour la première fois, votre héros est vraiment prêt. Quel est le plan et comment reflète-t-il sa transformation ?",
    forgeGuide_reckoning: "Votre héros exécute le plan dans un climax qui met à l'épreuve tout ce qu'il a appris. Le thème est prouvé, le défaut est surmonté et l'antagoniste — interne ou externe — est affronté de front. L'histoire de chaque personnage principal doit se résoudre ici. Faites en sorte que cela mérite chaque minute que le public a investie. Votre fin semble-t-elle à la fois surprenante ET inévitable ?",
    forgeGuide_lastFrame: "Une image finale qui reflète le Premier Cadre — mais tout a changé. Nous voyons le monde APRÈS. Votre protagoniste est transformé. La promesse de la Vérité Centrale a été tenue. Cette image doit durer. Elle doit ressembler à la dernière note d'une chanson qui se résout parfaitement. À quoi ressemble le monde de votre héros maintenant qu'il est devenu qui il était toujours destiné à être ?",

    // ── Common ──
    saving: 'Enregistrement...',
    close: 'Fermer',
    copy: 'Copier',
    copied: 'Copié',
    previous: 'Précédent',
    next: 'Suivant',
    back: 'Retour',
    generating: 'Génération...',

    // ── Dashboard ──
    dash_title: 'Mes Projets',
    dash_newProject: 'Nouveau Projet',
    dash_noProjects: 'Pas encore de projets — créez le premier ci-dessous',
    dash_emptyTitle: 'Aucun Projet',
    dash_emptyDesc: 'Créez votre premier projet pour commencer à construire votre histoire.',
    dash_createFirst: 'Créer Votre Premier Projet',
    dash_lastEdited: 'Dernière modification {{date}}',
    dash_progress: '{{pct}}% complété',
    dash_projects_one: '1 projet',
    dash_projects_other: '{{count}} projets',

    // ── Sidebar ──
    sidebar_back: 'Retour',
    sidebar_backToProjects: 'Retour à Mes Projets',
    sidebar_plot: 'Intrigue',
    sidebar_chars: 'Perso.',
    sidebar_scenes: 'Scènes',
    sidebar_script: 'Scénario',
    sidebar_world: 'Monde',
    sidebar_web: 'Réseau',
    sidebar_bible: 'Bible',
    sidebar_mentor: 'Mentor',
    sidebar_char: 'Perso.',
    sidebar_locked: 'Nécessite le plan Indie ou supérieur',

    // ── Navigation ──
    nav_backToDashboard: 'Tableau de Bord',
    nav_backToWorkspace: "Retour à l'Espace de Travail",
    nav_backToPlot: "Retour à l'Intrigue",

    // ── Plot Act titles ──
    plotAct1Title: 'Acte I : Départ',
    plotAct1Sub: 'Configuration',
    plotAct2Title: 'Acte II : Confrontation',
    plotAct2Sub: 'Action Montante',
    plotAct3Title: 'Acte III : Résolution',
    plotAct3Sub: 'Climax et Résolution',

    // ── Plot level UI ──
    plot_overallProgress: 'Progression Globale',
    plot_beatOf: 'Battement {{current}} sur {{total}}',
    plot_buildScenes: 'Construire les Scènes',
    plot_complete: "Compléter l'Intrigue",
    plot_nextBeat: 'Battement Suivant',

    // ── Beat placeholders ──
    beatPh_opening_image: "L'instantané de votre monde narratif avant que le changement ne commence",
    beatPh_setup: 'Présentez le protagoniste, son monde et ses relations initiales',
    beatPh_theme_stated: "Un indice sur la leçon ou la vérité que le protagoniste apprendra",
    beatPh_catalyst: "L'incident déclencheur qui perturbe le monde ordinaire",
    beatPh_debate: "Le protagoniste hésite ou questionne s'engager dans le voyage",
    beatPh_break_into_two: "Le protagoniste s'engage et entre dans un nouveau monde ou situation",
    beatPh_b_story: "Introduction d'une sous-intrigue ou d'une relation reflétant le thème",
    beatPh_fun_and_games: 'La promesse du récit — ce pour quoi le public est venu',
    beatPh_midpoint: 'Une victoire ou défaite majeure qui élève les enjeux',
    beatPh_bad_guys_close_in: 'Les forces externes et internes se resserrent autour du protagoniste',
    beatPh_all_is_lost: 'Le point le plus bas — fausse défaite ou revers majeur',
    beatPh_dark_night: 'Le protagoniste traite son échec avant la renaissance',
    beatPh_break_into_three: 'Le protagoniste trouve la solution ou la nouvelle approche',
    beatPh_finale: 'La confrontation finale où le protagoniste prouve sa transformation',
    beatPh_final_image: "Miroir de l'image d'ouverture montrant combien les choses ont changé",
    beatPh_obstacle: 'Le conflit ou défi principal s\'intensifie',
    beatPh_escalation: 'Les enjeux montent, les complications s\'accumulent',

    // ── Character Level titles ──
    charLevel0Title: 'Niveau 0 : Identité',
    charLevel0Sub: 'La Fondation',
    charLevel1Title: 'Niveau 1 : Le Plan Interne',
    charLevel1Sub: 'Âme et Origine',
    charLevel2Title: 'Niveau 2 : Le Monde et Introduction',
    charLevel2Sub: 'Configuration',
    charLevel3Title: 'Niveau 3 : La Cocotte-Minute',
    charLevel3Sub: 'Le Voyage',
    charLevel4Title: 'Niveau 4 : Le Règlement Final',
    charLevel4Sub: 'Résolution',
    charAntag1Title: 'Niveau 1 : Motivation et Logique Interne',
    charAntag1Sub: 'Pourquoi Ils Se Battent',
    charAntag2Title: 'Niveau 2 : Connexion avec le Protagoniste',
    charAntag2Sub: "L'Opposition",
    charAntag3Title: 'Niveau 3 : Pouvoir et Présence',
    charAntag3Sub: 'Menace et Agentivité',
    charAntag4Title: 'Niveau 4 : Complexité et Humanité',
    charAntag4Sub: 'Au-delà du Vilain',
    charSupp1Title: 'Niveau 1 : Le Pourquoi',
    charSupp1Sub: 'Objectif et Rôle',
    charSupp2Title: 'Niveau 2 : Le Qui',
    charSupp2Sub: 'Personnage et Profondeur',
    charSupp3Title: 'Niveau 3 : Le Comment',
    charSupp3Sub: 'Voix et Présence',
    charSupp4Title: 'Niveau 4 : La Croissance',
    charSupp4Sub: 'Arc et Impact',

    // ── Character question labels ──
    qLabel_name: 'Nom',
    qLabel_age: 'Âge',
    qLabel_gender: 'Genre',
    qLabel_ethnicity: 'Ethnie',
    qLabel_biography: 'Biographie Courte',
    qLabel_the_lie: 'Le Mensonge',
    qLabel_the_truth: 'La Vérité',
    qLabel_the_ghost: 'Le Fantôme (Optionnel pour Courts Métrages)',
    qLabel_wants: 'Désirs',
    qLabel_needs: 'Besoins',
    qLabel_needs_ignored: 'Besoins (Ignorés)',
    qLabel_corruption: 'La Corruption',
    qLabel_mission: 'Mission',
    qLabel_hero_narrative: 'Son Récit Héroïque',
    qLabel_tangible_goal: 'Objectif Tangible',
    qLabel_human_roots: 'Racines Humaines',
    qLabel_internal_logic: 'Logique Interne',
    qLabel_defined_role: 'Rôle Défini',
    qLabel_plot_impact: "Impact sur l'Intrigue",
    qLabel_the_trade: "L'Échange",
    qLabel_normal_world: 'Le Monde Normal',
    qLabel_persona: 'Persona',
    qLabel_core_trait: 'Trait Principal',
    qLabel_characteristic_moment: 'Moment Caractéristique',
    qLabel_direct_conflict: 'Conflit Direct',
    qLabel_mirror_effect: 'Effet Miroir',
    qLabel_targeted_pressure: 'Pression Ciblée',
    qLabel_the_wound: 'La Blessure',
    qLabel_offscreen_goal: 'Objectif Hors-Champ',
    qLabel_personal_stakes: 'Enjeux Personnels',
    qLabel_the_secret: 'Le Secret',
    qLabel_moral_compass: 'Boussole Morale',
    qLabel_inciting_event: 'Événement Déclencheur',
    qLabel_first_plot_point: "Premier Point d'Intrigue",
    qLabel_midpoint_char: 'Le Point Milieu',
    qLabel_third_plot_point: "Troisième Point d'Intrigue",
    qLabel_legitimate_threat: 'Menace Légitime',
    qLabel_proactive_agency: 'Agentivité Proactive',
    qLabel_early_victories: 'Victoires Précoces',
    qLabel_distinct_voice: 'Voix Distincte',
    qLabel_the_silhouette: 'La Silhouette',
    qLabel_vocal_anchor: 'Ancre Vocale',
    qLabel_habit_tic: 'Habitude / Tic',
    qLabel_entrance_exit: 'Entrée et Sortie',
    qLabel_climax: 'Le Climax',
    qLabel_new_normal: 'Le Nouveau Normal',
    qLabel_transformation: 'Transformation',
    qLabel_redeeming_trait: 'Trait Rédempteur',
    qLabel_vulnerability: 'Vulnérabilité',
    qLabel_no_caricatures: 'Pas de Caricatures',
    qLabel_the_right_point: 'Le Bon Point',
    qLabel_first_impression_vs_reality: 'Première Impression vs Réalité',
    qLabel_pressure_point: 'Point de Pression',
    qLabel_the_ending: 'La Fin',

    // ── Character question placeholders ──
    qPh_name: 'Nom complet du personnage',
    qPh_age: 'Quel est son âge ?',
    qPh_gender: 'Son identité de genre',
    qPh_ethnicity: 'Origines culturelles',
    qPh_biography: 'Qui, Quoi, Quand, Où, Pourquoi — Un bref aperçu de sa vie',
    qPh_the_lie: 'Quelle fausse croyance retient votre personnage ?',
    qPh_the_lie_neg: 'Quelle fausse croyance mènera à sa chute ?',
    qPh_the_truth: 'Quelle vérité doit-il découvrir ?',
    qPh_the_truth_flat: 'Quelle vérité votre personnage détient-il déjà ?',
    qPh_the_ghost: 'Quel traumatisme passé a créé le Mensonge ?',
    qPh_the_ghost_neg: 'Quel traumatisme passé pousse sa chute ?',
    qPh_the_ghost_flat: 'Quelle expérience a solidifié cette vérité ?',
    qPh_wants: 'Que pense vouloir votre personnage ?',
    qPh_wants_neg: 'Que désire votre personnage ?',
    qPh_wants_flat: 'Quel objectif externe le motive ?',
    qPh_needs: 'De quoi ont-ils vraiment besoin ?',
    qPh_needs_ignored: 'De quoi ont-ils vraiment besoin mais ignoreront-ils ?',
    qPh_corruption: 'À quelle tentation succomberont-ils ?',
    qPh_mission: 'Comment partageront-ils leur vérité avec le monde ?',
    qPh_hero_narrative: "Comment l'antagoniste se voit-il comme le héros de sa propre histoire ?",
    qPh_tangible_goal: 'Quel résultat concret poursuivent-ils ?',
    qPh_human_roots: 'Quelle expérience humaine ou quelle douleur motive ce personnage ?',
    qPh_internal_logic: 'Quel est le code interne qui justifie leurs actions ?',
    qPh_defined_role: 'Quelle fonction narrative ce personnage remplit-il ?',
    qPh_plot_impact: "Comment font-ils avancer l'histoire directement ?",
    qPh_the_trade: 'Que donnent-ils au protagoniste — et que prennent-ils ?',
    qPh_normal_world: 'Décrivez leur vie ordinaire avant le voyage',
    qPh_persona: 'Comment se présentent-ils au monde ?',
    qPh_core_trait: 'Quelle caractéristique définissante affichent-ils ?',
    qPh_characteristic_moment: 'Une scène qui montre qui ils sont maintenant',
    qPh_direct_conflict: "Comment ce personnage s'oppose-t-il directement au protagoniste ?",
    qPh_mirror_effect: "Comment reflètent-ils le moi obscur du protagoniste ou son pire avenir ?",
    qPh_targeted_pressure: 'Quelle faiblesse ou peur du protagoniste exploitent-ils ?',
    qPh_the_wound: "Quelle blessure personnelle fait de cet antagoniste ce qu'il est ?",
    qPh_offscreen_goal: "À quoi ressemble leur vie quand ils ne sont pas dans la scène ?",
    qPh_personal_stakes: "Qu'ont-ils à perdre dans le conflit de l'histoire ?",
    qPh_the_secret: 'Que cachent-ils — même au protagoniste ?',
    qPh_moral_compass: 'Que croient-ils juste ? Où est leur limite ?',
    qPh_inciting_event: 'Qu\'est-ce qui perturbe leur monde ordinaire ?',
    qPh_first_plot_point: 'Le point de non-retour',
    qPh_midpoint_char: 'Moment de Vérité — tout change',
    qPh_third_plot_point: 'Le moment de "Mort" — point le plus bas',
    qPh_legitimate_threat: 'Pourquoi ce personnage est-il un danger crédible pour le protagoniste ?',
    qPh_proactive_agency: "Comment font-ils avancer l'intrigue — sans juste réagir au protagoniste ?",
    qPh_early_victories: "Décrivez un moment où l'antagoniste gagne du terrain",
    qPh_distinct_voice: 'Qu\'est-ce qui rend leur dialogue et leur présence instantanément reconnaissables ?',
    qPh_the_silhouette: 'Décrivez leur apparence — quelque chose de visuellement distinctif',
    qPh_vocal_anchor: 'Une phrase, un rythme ou un schéma de discours qui leur est propre',
    qPh_habit_tic: 'Un comportement récurrent, un geste ou un rituel qui révèle le personnage',
    qPh_entrance_exit: 'Comment entrent-ils et quittent-ils généralement une scène ?',
    qPh_climax: 'Mise à l\'épreuve de la Vérité — confrontation finale',
    qPh_new_normal: 'Comment leur monde a-t-il changé ?',
    qPh_transformation: 'Comment ont-ils changé en tant que personne ?',
    qPh_redeeming_trait: 'Une qualité authentique qui les rend humains — pas sympathiques, juste réels',
    qPh_vulnerability: 'Qu\'est-ce qui peut briser leur armure ? Que craignent-ils ?',
    qPh_no_caricatures: 'Comment ce personnage évite-t-il de devenir un méchant de dessin animé ?',
    qPh_the_right_point: 'Quelle vérité, le cas échéant, cet antagoniste porte-t-il que le protagoniste (ou le public) doit affronter ?',
    qPh_first_impression_vs_reality: 'Comment votre première lecture diffère-t-elle de qui ils sont vraiment ?',
    qPh_pressure_point: 'Quelle situation les force à révéler leur vrai moi ?',
    qPh_the_ending: 'Où finissent-ils ? Comment l\'histoire les a-t-elle changés — même légèrement ?',

    // ── Scene Builder ──
    scene_builder: 'Constructeur de Scènes',
    scene_newScene: 'Nouvelle Scène',
    scene_editScene: 'Modifier la Scène',
    scene_addFirst: 'Ajouter la Première Scène',
    scene_noScenes: "Aucune scène pour l'instant",
    scene_noScenesDesc: 'Ajoutez des scènes pour décomposer vos battements en moments filmables.',
    scene_noScenesBeat: 'Ajoutez des scènes pour "{{beat}}" pour les décomposer en moments filmables.',
    scene_titleLabel: 'Titre de la Scène',
    scene_titleRequired: 'Titre de la Scène *',
    scene_titlePh: 'INT. MARCHÉ — JOUR',
    scene_locationLabel: 'Lieu',
    scene_locationPh: 'INT. MARCHÉ DE NAIROBI — HEURE DORÉE',
    scene_plotProjectLabel: "Projet d'Intrigue",
    scene_beatLabel: 'Battement',
    scene_standalone: 'Autonome',
    scene_none: 'Aucun',
    scene_bmoc: 'B.M.O.C',
    scene_actionLabel: 'ACTION — Que se passe-t-il physiquement dans cette scène ?',
    scene_actionPh: "Décrivez l'action physique principale de la scène",
    scene_wantVsNeedLabel: 'DÉSIR vs BESOIN — Que veut le personnage, et de quoi a-t-il vraiment besoin ?',
    scene_wantVsNeedPh: 'Désir de surface vs nécessité profonde',
    scene_conflictLabel: "CONFLIT — Quelle force (personne, situation ou interne) s'y oppose ?",
    scene_conflictPh: 'La force antagoniste qui résiste',
    scene_turnLabel: 'TOURNANT — Comment la scène se termine-t-elle différemment de comment elle a commencé ?',
    scene_turnPh: "Le changement de situation, d'émotion ou de connaissance",
    scene_functionLabel: 'Fonction de la Scène',
    scene_fnSelectPh: '— Sélectionner une fonction —',
    scene_fnIntroduce: 'Introduire',
    scene_fnEscalate: 'Escalader',
    scene_fnReveal: 'Révéler',
    scene_fnConfront: 'Confronter',
    scene_fnResolve: 'Résoudre',
    scene_polarityShift: 'Changement de Polarité',
    scene_polarityStartPh: 'Espoir',
    scene_polarityEndPh: 'Désespoir',
    scene_notes: 'Notes',
    scene_notesPh: 'Notes de production, idées de plans, références de ton...',
    scene_saveScene: 'Enregistrer la Scène',
    scene_scenesCount: '{{count}} / {{limit}} scènes',
    scene_action_card: 'Action',
    scene_wantVsNeed_card: 'Désir vs Besoin',
    scene_conflict_card: 'Conflit',
    scene_turn_card: 'Tournant',
    scene_function_card: 'Fonction de la Scène',
    scene_beatPrefix: 'Battement : ',

    // ── Project Workspace ──
    ws_overallProgress: 'Progression Globale',
    ws_act1: 'Acte I',
    ws_act2: 'Acte II',
    ws_act3: 'Acte III',
    ws_act1Desc: 'Configuration',
    ws_act2Desc: 'Action Montante',
    ws_act3Desc: 'Résolution',
    ws_editAct: "Modifier l'Acte {{n}}",
    ws_completedBeats: '{{done}} / {{total}} battements',
    ws_aiLogline: 'Logline & Synopsis IA',
    ws_aiLoglineDesc: 'Générez une phrase de présentation et un synopsis en 3 actes à partir de votre liste de battements.',
    ws_generateAI: 'Générer avec IA',
    ws_generating: 'Génération...',
    ws_logline: 'Logline',
    ws_synopsis: 'Synopsis',
    ws_copy: 'Copier',
    ws_copied: 'Copié',
    ws_3LeftThisMonth: '{{n}} sur 3 restants ce mois',
    ws_monthlyGenerations: '3 générations mensuelles',

    // ── Plot Mentor ──
    pm_title: "Mentor d'Intrigue",
    pm_desc: "Coaching IA sur la structure de votre intrigue et la logique narrative",
    pm_forgeGuide: 'Guide Forge',
    pm_forgeGuideDesc: 'Coaching socratique — battement par battement',
    pm_stressTest: 'Test de Stress',
    pm_stressTestDesc: 'Challengez les incohérences et les lacunes logiques',
    pm_startForgeGuide: 'Démarrer le Guide Forge',
    pm_runStressTest: 'Lancer le Test de Stress',
    pm_readingPlot: "Lecture de vos données d'intrigue...",
    pm_thinking: 'En train de réfléchir...',
    pm_replyPh: 'Répondez à votre mentor...',
    pm_fullStory: 'Histoire Complète',
    pm_act1: 'Acte I',
    pm_act2: 'Acte II',
    pm_act3: 'Acte III',
    pm_usesLeft: '{{n}} sur 3 restants ce mois',
    pm_close: 'Fermer',

    // ── AI Mentor UI ──
    mentor_replyPh: 'Demandez à votre mentor...',
    mentor_thinking: 'Votre mentor réfléchit...',
    mentor_proFeature: 'Le Mentor IA est une fonctionnalité Pro',
    mentor_upgradeBtn: 'Passer à Pro',
    mentor_topicTrauma: 'Trauma',
    mentor_topicLie: 'Le Mensonge',
    mentor_topicTruth: 'La Vérité',
    mentor_topicWants: 'Désirs',
    mentor_topicNeeds: 'Besoins',
    mentor_topicRelationships: 'Relations',
    mentor_topicConflict: 'Conflit',
    mentor_level_identity: 'Identité',
    mentor_level_internalBlueprint: 'Plan Interne',
    mentor_level_worldIntro: 'Monde et Intro',
    mentor_level_pressureCooker: 'Cocotte-Minute',
    mentor_level_finalReckoning: 'Règlement Final',

    // ── Export ──
    export_title: "Exporter la Bible de l'Histoire",
    export_sections: 'Sections',
    export_filename: 'Nom du Fichier',
    export_download: 'Télécharger le PDF',
    export_download_as: 'Télécharger {{format}}',
    export_downloading: 'Génération…',
    export_preview: 'Aperçu',
    export_noSections: 'Sélectionnez des sections à gauche pour prévisualiser le contenu.',

    // ── Character Level chrome ──
    char_roleDesc: 'Définit comment ce personnage est positionné dans Le Réseau',
    char_arcTypeDesc: 'Détermine les questions du Niveau 1 pour le plan interne',
    char_completeContinue: 'Compléter et Continuer',
    char_completeCharacter: 'Compléter le Personnage',
    char_enneagramLabel: 'Test de Stress Ennéagramme',
    char_enneagramPh: "Sélectionner l'Archétype de Personnalité",
    char_upgradeToIndie: 'Passer à Indie',
    char_enneagramSyncNote: 'Cela synchronisera les modèles de stress avec votre battement "Le Point de Rupture" de Plot Forge',
    char_enneagramLockedNote: 'Débloquez la synchronisation de personnalité inter-tableaux avec le plan Indie ou supérieur',

    // ── Enneagram types ──
    enn_type1: 'Type 1 : Le Perfectionniste',
    enn_type2: "Type 2 : L'Aidant",
    enn_type3: "Type 3 : L'Accompli",
    enn_type4: "Type 4 : L'Individualiste",
    enn_type5: "Type 5 : L'Investigateur",
    enn_type6: 'Type 6 : Le Loyal',
    enn_type7: "Type 7 : L'Enthousiaste",
    enn_type8: 'Type 8 : Le Meneur',
    enn_type9: 'Type 9 : Le Pacificateur',

    // ── Arc Helper content ──
    arcHelper_positive_title: 'Arc de Changement (Positif)',
    arcHelper_positive_desc: 'Votre personnage surmonte son Mensonge et embrasse la Vérité, grandissant en tant que personne.',
    arcHelper_positive_g0: 'Le Mensonge est une fausse croyance qui le retient',
    arcHelper_positive_g1: "La Vérité est ce qu'il doit découvrir pour grandir",
    arcHelper_positive_g2: "Le Fantôme est l'événement passé qui a créé le Mensonge",
    arcHelper_positive_g3: "Il VEUT quelque chose d'externe, mais a BESOIN d'une croissance interne",
    arcHelper_positive_t0: 'Faites en sorte que le Mensonge semble vrai pour votre personnage',
    arcHelper_positive_t1: 'La Vérité doit directement contredire le Mensonge',
    arcHelper_positive_t2: 'Son Désir et son Besoin entrent souvent en conflit',
    arcHelper_negative_title: 'Arc de Chute (Négatif)',
    arcHelper_negative_desc: 'Votre personnage succombe à son Mensonge, menant à sa chute ou sa corruption.',
    arcHelper_negative_g0: 'Le Mensonge devient leur justification de mauvais choix',
    arcHelper_negative_g1: 'La Corruption est la tentation à laquelle ils ne peuvent résister',
    arcHelper_negative_g2: 'Le Fantôme alimente leur descente',
    arcHelper_negative_g3: "Ils ignorent ce dont ils ont vraiment besoin",
    arcHelper_negative_t0: 'Rendez la corruption compréhensible',
    arcHelper_negative_t1: "Montrez ce qu'ils auraient pu être",
    arcHelper_negative_t2: "La tragédie est dans ce qu'ils perdent",
    arcHelper_flat_title: 'Arc Plat',
    arcHelper_flat_desc: "Votre personnage connaît déjà la Vérité et l'utilise pour changer le monde autour de lui.",
    arcHelper_flat_g0: 'Ils possèdent une Vérité inébranlable',
    arcHelper_flat_g1: 'Le Fantôme a solidifié cette Vérité',
    arcHelper_flat_g2: 'Leur mission est de partager cette Vérité',
    arcHelper_flat_g3: "Les obstacles externes mettent à l'épreuve leur conviction",
    arcHelper_flat_t0: 'Le monde challenge leur Vérité',
    arcHelper_flat_t1: "D'autres personnages peuvent changer à cause d'eux",
    arcHelper_flat_t2: 'Leur conviction doit être testée mais pas brisée',

    // ── ProjectWorkspace ──
    ws_setup: 'Configuration',
    ws_loglineSynopsis: 'Logline & Synopsis',
    ws_addLogline: 'Ajoutez votre logline et synopsis',
    ws_buildBeatSheet: 'Construisez votre liste de battements acte par acte.',
    ws_allCharacters: 'Tous les personnages de votre bibliothèque.',
    ws_noCharacters: 'Aucun Personnage',
    ws_noCharactersDesc: 'Ajoutez des personnages pour peupler votre histoire.',
    ws_createFirstChar: 'Créer le Premier Personnage',
    ws_scenesDesc: 'Scènes attachées à cette intrigue.',
    ws_noScenes: 'Aucune Scène',
    ws_noScenesDesc: 'Construisez des scènes pour chaque battement de votre intrigue.',
    ws_openSceneBuilder: 'Ouvrir le Constructeur de Scènes',
    ws_indieFeature: 'Fonctionnalité Indie',
    ws_webIndieDesc: 'Le Réseau de Personnages est disponible avec le plan Indie et supérieur.',
    ws_webDesc: 'Cartographiez les relations et les arguments thématiques entre les personnages.',
    export_sparkNote: 'Spark — PDF inclut un filigrane',
  }
};

// Italian translations
const it = {
  translation: {
    export_download_as: 'Scarica {{format}}',
    export_downloading: 'Generazione…',
    dashboard: 'Pannello',
    characterLab: 'Laboratorio Personaggi',
    plotForge: 'Fucina della Trama',
    theWeb: 'La Rete',
    worldBuilding: 'Costruzione del Mondo',
    logout: 'Esci',
    upgrade: 'Passa a Pro',
    settings: 'Impostazioni',
    
    level0Title: 'Livello 0: Identità',
    level0Subtitle: 'Le Fondamenta',
    level1Title: 'Livello 1: Il Progetto Interno',
    level1Subtitle: 'Anima e Origine',
    level2Title: 'Livello 2: Il Mondo e Introduzione',
    level2Subtitle: 'Configurazione',
    level3Title: 'Livello 3: La Pentola a Pressione',
    level3Subtitle: 'Il Viaggio',
    level4Title: 'Livello 4: La Resa dei Conti',
    level4Subtitle: 'Risoluzione',
    
    name: 'Nome',
    age: 'Età',
    gender: 'Genere',
    ethnicity: 'Etnia',
    shortBiography: 'Breve Biografia',
    role: 'Ruolo',
    arcType: 'Tipo di Arco',
    
    protagonist: 'Protagonista',
    antagonist: 'Antagonista',
    supporting: 'Secondario',
    
    changePositive: 'Cambiamento (Positivo)',
    fallNegative: 'Caduta (Negativo)',
    flat: 'Piatto',
    
    arcHelper: 'Assistente Arco',
    keyElements: 'Elementi Chiave',
    proTips: 'Consigli Pro',
    
    theLie: 'La Bugia',
    theTruth: 'La Verità',
    theGhost: 'Il Fantasma',
    wants: 'Desideri',
    needs: 'Bisogni',
    corruption: 'La Corruzione',
    mission: 'Missione',
    
    characterRelationshipMatrix: 'Matrice Relazioni',
    addRelationship: 'Aggiungi Relazione',
    characterA: 'Personaggio A',
    characterB: 'Personaggio B',
    moralDifference: 'Differenza Morale',
    tacticalDifference: 'Differenza Tattica',
    centralArgument: 'Argomento Centrale',
    conflictGauge: 'Misuratore Conflitto',
    
    locations: 'Luoghi',
    costumes: 'Costumi',
    props: 'Oggetti di Scena',
    description: 'Descrizione',
    vibe: 'Atmosfera',
    plotSignificance: 'Significato nella Trama',
    aesthetic: 'Estetica',
    evolution: 'Evoluzione',
    owner: 'Proprietario',
    symbolicValue: 'Valore Simbolico',
    function: 'Funzione',
    
    save: 'Salva',
    saveProgress: 'Salva Progressi',
    saveStatus_saving: 'Salvataggio…',
    saveStatus_dirty: 'Modifiche non salvate…',
    saveStatus_notSaving: 'NON SALVA — copia il tuo lavoro',
    saveStatus_retrying: 'Salvataggio non riuscito — nuovo tentativo…',
    saveStatus_saved: 'Salvato',
    saveStatus_savedAt: 'Salvato {{time}}',
    saveStatus_conflict: 'Questo progetto è stato modificato altrove — ricarica questa scheda per continuare',
    milestone_developed: '{{name}} è completamente sviluppato.',
    char_completeLevel: 'Completa il Livello {{n}}',
    scene_unassigned: 'Non assegnate',
    char_depthDeveloped: '{{levels}} sviluppati',
    autosave_navBlocked: 'Le tue modifiche non sono ancora salvate — copia il tuo lavoro e riprova',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    newCharacter: 'Nuovo Personaggio',
    newPlot: 'Nuova Trama',
    exportPdf: 'Esporta PDF',
    stressTest: 'Test di Stress',
    aiMentor: 'Mentore IA',
    
    storyAuditor: 'Revisore Storia',
    runStressTest: 'Esegui Test',
    storyHealthScore: 'Punteggio Salute',
    issuesFound: 'Problemi Trovati',
    suggestions: 'Suggerimenti',
    strengths: 'Punti di Forza',
    
    upgradeToPro: 'Passa a Pro',
    monthly: 'Mensile',
    yearly: 'Annuale',
    perMonth: '/mese',
    perYear: '/anno',
    subscribe: 'Abbonati',
    
    loading: 'Caricamento...',
    saved: 'Salvato',
    error: 'Qualcosa è andato storto',
    proFeature: 'Funzione Pro',

    showForgeGuide: 'Mostra Guida Forge',
    hideForgeGuide: 'Nascondi Guida Forge',

    forgeGuide_firstFrame: "Questa è la primissima impressione del tuo mondo. Mostraci uno spaccato della vita del tuo protagonista PRIMA che tutto cambi. Dovrebbe essere uno specchio visivo ed emotivo di dove finiranno — il pubblico non lo saprà ancora, ma tu sì. Chiediti: come appare la normalità per questa persona e quanto distano da chi devono diventare?",
    forgeGuide_coreTruth: "Da qualche parte nel tuo primo atto, un personaggio — spesso non l'eroe — dice qualcosa che è la colonna vertebrale tematica dell'intera tua storia. Il tuo protagonista probabilmente non sarà d'accordo ora. Alla fine, lo capirà completamente. Qual è l'unica verità che la tua storia cerca di dimostrare?",
    forgeGuide_worldBefore: "Presenta il tuo protagonista nel suo elemento. Dobbiamo vedere il suo mondo, le sue relazioni, i suoi difetti e il suo bisogno insoddisfatto. Sei cose devono essere stabilite qui: personaggio, posta in gioco, genere, tono, il difetto che il tuo eroe deve superare e il mondo che sta per essere sconvolto. Prenditi il tempo — questo è l'ultimo momento di pace.",
    forgeGuide_spark: "Accade qualcosa che il protagonista NON ha causato e non può ignorare. Questo è l'evento che bussa alla porta del loro mondo ordinario e chiede risposta. Dovrebbe sembrare sia sorprendente che inevitabile. Non è una decisione — è l'universo che fa una mossa. Cosa accade al tuo eroe che costringe la storia a iniziare?",
    forgeGuide_crossroads: "Il tuo eroe sa che la Scintilla è accaduta, ma non è pronto ad agire. Questo è il combattimento interiore — devo andare o restare? Più lungo e doloroso è questo dibattito, più il pubblico si investe. Il tuo eroe ha bisogno di una spinta finale — un'informazione cruciale, una minaccia o un momento di coraggio — per impegnarsi definitivamente. Cosa li ferma e cosa li fa muovere alla fine?",
    forgeGuide_leap: "Il tuo protagonista fa una scelta attiva e consapevole di entrare in un nuovo mondo — letteralmente o metaforicamente. La storia dell'Atto I è finita. Inizia una nuova storia. Questo dovrebbe sembrare un punto di non ritorno. Il pubblico dovrebbe percepire che nulla sarà mai lo stesso. Dove il tuo eroe attraversa la soglia e cosa lascia dietro di sé?",
    forgeGuide_heartThread: "Questa è la tua sottotrama emotiva — solitamente una relazione che porta il tema. Mentre la storia A riguarda ciò che l'eroe VUOLE, il Filo del Cuore riguarda ciò di cui ha BISOGNO. Spesso un mentore, un interesse romantico o un'amicizia inaspettata. Questa relazione insegnerà al tuo protagonista la Verità Centrale alla fine. Chi è questa persona e cosa rappresenta che al tuo eroe manca?",
    forgeGuide_promise: "Questo è il motivo per cui la gente ha comprato il biglietto. Il divertimento, i brividi, il romanticismo, gli spaventi — qualunque cosa prometta il tuo genere, consegnala qui. Il tuo protagonista è nel nuovo mondo e lo sta esplorando. Le cose non vanno perfettamente, ma non stanno ancora crollando. Questa è la sezione più divertente del tuo film. Stai mantenendo la promessa che il tuo genere ha fatto al pubblico?",
    forgeGuide_turningTide: "A metà della tua storia, qualcosa cambia significativamente. Il tuo eroe ha una falsa vittoria o una falsa sconfitta — dove le cose sembrano terribili ma l'eroe trova la sua lotta. La posta raddoppia qui. Il tuo protagonista deve smettere di reagire e iniziare a guidare la storia. Cosa cambia al punto medio che rende la seconda metà un tipo di storia completamente diverso?",
    forgeGuide_wallsClosing: "Il tuo eroe sta perseguendo il suo obiettivo, ma ora ogni forza — interna ed esterna — spinge indietro. Dubbio, nemici, cattiva fortuna, tradimento. La tensione deve sembrare soffocante. Il pubblico deve chiedersi davvero se il tuo eroe ce la farà. Non sono difficoltà casuali — ogni ostacolo dovrebbe esporre un difetto più profondo che l'eroe non ha ancora affrontato. Chi o cosa si avvicina, e perché fa più male proprio ora?",
    forgeGuide_breakingPoint: "Il tuo eroe ha perso. Non solo l'obiettivo esterno — ha perso la cosa che contava di più emotivamente. Una relazione, una convinzione, un mentore, una parte di se stesso. Questo è il momento più basso della storia. Dovrebbe sembrare definitivo. Il pubblico deve credere, anche solo per un momento, che non ci sia via d'uscita. Qual è la cosa peggiore che potrebbe accadere al tuo eroe adesso — e falla accadere.",
    forgeGuide_hollow: "Dopo il Punto di Rottura, il tuo eroe siede tra le macerie. Questo è un momento di quiete prima della tempesta. Devono confrontarsi con la menzogna che hanno vissuto e scegliere — consciamente e dolorosamente — di abbracciare la Verità Centrale. Questa è l'anima della tua storia. Nessuno può salvarli qui. Devono salvarsi da soli. Cosa realizza il tuo eroe nel loro momento più buio?",
    forgeGuide_rising: "Il tuo eroe emerge trasformato. Armato della verità, formula un piano finale. Gli strumenti vecchi non funzioneranno — devono usare chi sono DIVENTATI, non chi erano. La storia A e il Filo del Cuore iniziano a convergere. La battaglia finale si avvicina e, per la prima volta, il tuo eroe è davvero pronto. Qual è il piano e come riflette la sua crescita?",
    forgeGuide_reckoning: "Il tuo eroe esegue il piano in un climax che mette alla prova tutto ciò che ha imparato. Il tema viene dimostrato, il difetto viene conquistato e l'antagonista — interno o esterno — viene affrontato direttamente. La storia di ogni personaggio principale dovrebbe risolversi qui. Falla meritare ogni minuto che il pubblico ha investito. Il tuo finale sembra sia sorprendente CHE inevitabile?",
    forgeGuide_lastFrame: "Un'immagine finale che rispecchia il Primo Fotogramma — ma tutto è cambiato. Vediamo il mondo DOPO. Il tuo protagonista è trasformato. La promessa della Verità Centrale è stata mantenuta. Questa immagine dovrebbe durare. Dovrebbe sembrare l'ultima nota di una canzone che si risolve perfettamente. Come appare il mondo del tuo eroe ora che è diventato chi era sempre destinato a essere?",
  }
};

// Portuguese translations
const pt = {
  translation: {
    export_download_as: 'Baixar {{format}}',
    export_downloading: 'Gerando…',
    dashboard: 'Painel',
    characterLab: 'Laboratório de Personagens',
    plotForge: 'Forja de Enredo',
    theWeb: 'A Rede',
    worldBuilding: 'Construção do Mundo',
    logout: 'Sair',
    upgrade: 'Atualizar para Pro',
    settings: 'Configurações',
    
    level0Title: 'Nível 0: Identidade',
    level0Subtitle: 'A Fundação',
    level1Title: 'Nível 1: O Projeto Interno',
    level1Subtitle: 'Alma e Origem',
    level2Title: 'Nível 2: O Mundo e Introdução',
    level2Subtitle: 'Configuração',
    level3Title: 'Nível 3: A Panela de Pressão',
    level3Subtitle: 'A Jornada',
    level4Title: 'Nível 4: O Acerto Final',
    level4Subtitle: 'Resolução',
    
    name: 'Nome',
    age: 'Idade',
    gender: 'Gênero',
    ethnicity: 'Etnia',
    shortBiography: 'Biografia Curta',
    role: 'Papel',
    arcType: 'Tipo de Arco',
    
    protagonist: 'Protagonista',
    antagonist: 'Antagonista',
    supporting: 'Coadjuvante',
    
    changePositive: 'Mudança (Positivo)',
    fallNegative: 'Queda (Negativo)',
    flat: 'Plano',
    
    arcHelper: 'Assistente de Arco',
    keyElements: 'Elementos Chave',
    proTips: 'Dicas Pro',
    
    theLie: 'A Mentira',
    theTruth: 'A Verdade',
    theGhost: 'O Fantasma',
    wants: 'Desejos',
    needs: 'Necessidades',
    corruption: 'A Corrupção',
    mission: 'Missão',
    
    characterRelationshipMatrix: 'Matriz de Relações',
    addRelationship: 'Adicionar Relação',
    characterA: 'Personagem A',
    characterB: 'Personagem B',
    moralDifference: 'Diferença Moral',
    tacticalDifference: 'Diferença Tática',
    centralArgument: 'Argumento Central',
    conflictGauge: 'Medidor de Conflito',
    
    locations: 'Locações',
    costumes: 'Figurinos',
    props: 'Adereços',
    description: 'Descrição',
    vibe: 'Atmosfera',
    plotSignificance: 'Significado no Enredo',
    aesthetic: 'Estética',
    evolution: 'Evolução',
    owner: 'Proprietário',
    symbolicValue: 'Valor Simbólico',
    function: 'Função',
    
    save: 'Salvar',
    saveProgress: 'Salvar Progresso',
    saveStatus_saving: 'Salvando…',
    saveStatus_dirty: 'Alterações não salvas…',
    saveStatus_notSaving: 'NÃO ESTÁ SALVANDO — copie seu trabalho',
    saveStatus_retrying: 'Falha ao salvar — tentando novamente…',
    saveStatus_saved: 'Salvo',
    saveStatus_savedAt: 'Salvo {{time}}',
    saveStatus_conflict: 'Este projeto foi alterado em outro lugar — recarregue esta guia para continuar',
    milestone_developed: '{{name}} está totalmente desenvolvido.',
    char_completeLevel: 'Concluir Nível {{n}}',
    scene_unassigned: 'Não atribuídas',
    char_depthDeveloped: '{{levels}} desenvolvidos',
    autosave_navBlocked: 'Suas alterações ainda não foram salvas — copie seu trabalho e tente novamente',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    newCharacter: 'Novo Personagem',
    newPlot: 'Novo Enredo',
    exportPdf: 'Exportar PDF',
    stressTest: 'Teste de Estresse',
    aiMentor: 'Mentor IA',
    
    storyAuditor: 'Auditor de História',
    runStressTest: 'Executar Teste',
    storyHealthScore: 'Pontuação de Saúde',
    issuesFound: 'Problemas Encontrados',
    suggestions: 'Sugestões',
    strengths: 'Pontos Fortes',
    
    upgradeToPro: 'Atualizar para Pro',
    monthly: 'Mensal',
    yearly: 'Anual',
    perMonth: '/mês',
    perYear: '/ano',
    subscribe: 'Assinar',
    
    loading: 'Carregando...',
    saved: 'Salvo',
    error: 'Algo deu errado',
    proFeature: 'Recurso Pro',

    showForgeGuide: 'Mostrar Guia Forge',
    hideForgeGuide: 'Ocultar Guia Forge',

    forgeGuide_firstFrame: "Esta é a primeiríssima impressão do seu mundo. Mostre-nos um instantâneo da vida do seu protagonista ANTES de tudo mudar. Deve ser um espelho visual e emocional de onde eles acabarão — o público ainda não saberá, mas você sabe. Pergunte-se: como é a normalidade para essa pessoa e quão distante ela está de quem precisa se tornar?",
    forgeGuide_coreTruth: "Em algum lugar no seu primeiro ato, um personagem — muitas vezes não o herói — diz algo que é a espinha dorsal temática de toda a sua história. Seu protagonista provavelmente discordará disso agora. No final, entenderá completamente. Qual é a única verdade que sua história está tentando provar?",
    forgeGuide_worldBefore: "Apresente seu protagonista em seu elemento. Precisamos ver seu mundo, seus relacionamentos, suas falhas e sua necessidade não atendida. Seis coisas devem ser estabelecidas aqui: personagem, apostas, gênero, tom, a falha que seu herói deve superar e o mundo que está prestes a ser perturbado. Tome seu tempo — este é o último momento de paz.",
    forgeGuide_spark: "Algo acontece que o protagonista NÃO causou e não pode ignorar. Este é o evento que bate na porta do mundo ordinário deles e exige uma resposta. Deve parecer tanto surpreendente quanto inevitável. Não é uma decisão — é o universo fazendo uma jogada. O que acontece COM seu herói que força a história a começar?",
    forgeGuide_crossroads: "Seu herói sabe que a Centelha aconteceu, mas não está pronto para agir. Este é o combate interno — devo ir ou ficar? Quanto mais longo e doloroso for esse debate, mais o público se investe. Seu herói precisa de um impulso final — informação, uma ameaça ou um momento de coragem — para finalmente se comprometer. O que os detém e o que finalmente os move?",
    forgeGuide_leap: "Seu protagonista faz uma escolha ativa e consciente de entrar em um novo mundo — literal ou metaforicamente. A história do Ato I acabou. Uma nova história começa. Isso deve parecer um ponto sem retorno. O público deve perceber que nada jamais será o mesmo. Onde seu herói cruza o limiar e o que ele deixa para trás?",
    forgeGuide_heartThread: "Esta é a sua subtrama emocional — geralmente um relacionamento que carrega o tema. Enquanto a história A é sobre o que o herói QUER, o Fio do Coração é sobre o que ele PRECISA. Frequentemente um mentor, interesse amoroso ou amizade inesperada. Este relacionamento ensinará ao seu protagonista a Verdade Central no final. Quem é essa pessoa e o que ela representa que seu herói está perdendo?",
    forgeGuide_promise: "É por isso que as pessoas compraram o ingresso. A diversão, os sustos, o romance, os medos — seja o que for que seu gênero prometa, entregue aqui. Seu protagonista está no novo mundo e o explorando. As coisas não vão perfeitamente, mas ainda não estão desmoronando. Esta é a seção mais divertida do seu filme. Você está cumprindo a promessa que seu gênero fez ao público?",
    forgeGuide_turningTide: "Na metade da sua história, algo muda significativamente. Seu herói tem uma falsa vitória ou uma falsa derrota — onde as coisas parecem terríveis mas o herói encontra sua luta. As apostas dobram aqui. Seu protagonista deve parar de reagir e começar a conduzir a história. O que muda no ponto médio que faz a segunda metade ser um tipo de história completamente diferente?",
    forgeGuide_wallsClosing: "Seu herói está perseguindo seu objetivo, mas agora cada força — interna e externa — está resistindo. Dúvida, inimigos, má sorte, traição. A tensão deve parecer sufocante. O público deve genuinamente questionar se seu herói conseguirá. Não é dificuldade aleatória — cada obstáculo deve expor uma falha mais profunda que o herói ainda não enfrentou. Quem ou o que está se fechando, e por que dói mais agora?",
    forgeGuide_breakingPoint: "Seu herói perdeu. Não apenas o objetivo externo — eles perderam a coisa que mais importava emocionalmente. Um relacionamento, uma crença, um mentor, uma parte de si mesmo. Este é o ponto mais baixo da história. Deve parecer definitivo. O público deve acreditar, mesmo que por um momento, que não há caminho de volta. Qual é a pior coisa que poderia acontecer ao seu herói agora — e faça acontecer.",
    forgeGuide_hollow: "Após o Ponto de Ruptura, seu herói senta nos escombros. Este é um momento de quietude antes da tempestade. Eles devem confrontar a mentira que viveram e escolher — consciente e dolorosamente — abraçar a Verdade Central. Esta é a alma da sua história. Ninguém pode salvá-los aqui. Eles devem se salvar. O que seu herói percebe em seu momento mais sombrio?",
    forgeGuide_rising: "Seu herói emerge transformado. Armado com a verdade, formula um plano final. As ferramentas antigas não funcionarão — eles devem usar quem se TORNARAM, não quem eram. A história A e o Fio do Coração começam a convergir. A batalha final está chegando e, pela primeira vez, seu herói está verdadeiramente pronto. Qual é o plano e como ele reflete seu crescimento?",
    forgeGuide_reckoning: "Seu herói executa o plano em um clímax que testa tudo o que aprendeu. O tema é provado, a falha é conquistada e o antagonista — interno ou externo — é enfrentado de frente. A história de cada personagem principal deve se resolver aqui. Faça-a merecer cada minuto que o público investiu. Seu final parece tanto surpreendente QUANTO inevitável?",
    forgeGuide_lastFrame: "Uma imagem final que espelha o Primeiro Quadro — mas tudo mudou. Vemos o mundo DEPOIS. Seu protagonista está transformado. A promessa da Verdade Central foi cumprida. Esta imagem deve durar. Deve parecer a última nota de uma música que se resolve perfeitamente. Como é o mundo do seu herói agora que ele se tornou quem sempre deveria ser?",
  }
};

// Arabic translations
const ar = {
  translation: {
    // Navigation
    dashboard: 'لوحة التحكم',
    characterLab: 'مختبر الشخصيات',
    plotForge: 'مصنع الحبكة',
    theWeb: 'الشبكة',
    worldBuilding: 'بناء العالم',
    logout: 'تسجيل الخروج',
    upgrade: 'الترقية إلى Pro',
    settings: 'الإعدادات',

    // Character Levels
    level0Title: 'المستوى 0: الهوية',
    level0Subtitle: 'الأساس',
    level1Title: 'المستوى 1: المخطط الداخلي',
    level1Subtitle: 'الروح والأصل',
    level2Title: 'المستوى 2: العالم والمقدمة',
    level2Subtitle: 'الإعداد',
    level3Title: 'المستوى 3: قدر الضغط',
    level3Subtitle: 'الرحلة',
    level4Title: 'المستوى 4: الحساب الأخير',
    level4Subtitle: 'الحل',

    // Form Labels
    name: 'الاسم',
    age: 'العمر',
    gender: 'الجنس',
    ethnicity: 'العرق',
    shortBiography: 'سيرة ذاتية مختصرة',
    role: 'الدور',
    arcType: 'نوع القوس',

    // Role Options
    protagonist: 'البطل',
    antagonist: 'الخصم',
    supporting: 'شخصية مساعدة',

    // Arc Types
    changePositive: 'تحوّل (إيجابي)',
    fallNegative: 'سقوط (سلبي)',
    flat: 'ثابت',

    // Arc Helper
    arcHelper: 'مساعد القوس',
    keyElements: 'العناصر الأساسية',
    proTips: 'نصائح الخبراء',

    // Level 1 Questions
    theLie: 'الكذبة',
    theTruth: 'الحقيقة',
    theGhost: 'الشبح',
    wants: 'الرغبات',
    needs: 'الاحتياجات',
    corruption: 'الفساد',
    mission: 'المهمة',

    // Character Web
    characterRelationshipMatrix: 'مصفوفة علاقات الشخصيات',
    addRelationship: 'إضافة علاقة',
    characterA: 'الشخصية أ',
    characterB: 'الشخصية ب',
    moralDifference: 'الاختلاف الأخلاقي',
    tacticalDifference: 'الاختلاف التكتيكي',
    centralArgument: 'الحجة المحورية',
    conflictGauge: 'مقياس الصراع',

    // World Building
    locations: 'المواقع',
    costumes: 'الأزياء',
    props: 'الإكسسوارات',
    description: 'الوصف',
    vibe: 'الأجواء',
    plotSignificance: 'الأهمية في الحبكة',
    aesthetic: 'الجماليات',
    evolution: 'التطور',
    owner: 'المالك',
    symbolicValue: 'القيمة الرمزية',
    function: 'الوظيفة',

    // Buttons
    save: 'حفظ',
    saveProgress: 'حفظ التقدم',
    saveStatus_saving: 'جارٍ الحفظ…',
    saveStatus_dirty: 'تغييرات غير محفوظة…',
    saveStatus_notSaving: 'لا يتم الحفظ — انسخ عملك',
    saveStatus_retrying: 'فشل الحفظ — تتم إعادة المحاولة…',
    saveStatus_saved: 'تم الحفظ',
    saveStatus_savedAt: 'تم الحفظ {{time}}',
    saveStatus_conflict: 'تم تغيير هذا المشروع في مكان آخر — أعد تحميل علامة التبويب هذه للمتابعة',
    milestone_developed: '{{name}} مُطوَّر بالكامل.',
    char_completeLevel: 'أكمل المستوى {{n}}',
    scene_unassigned: 'غير مُخصَّصة',
    char_depthDeveloped: '{{levels}} مُطوَّرة',
    autosave_navBlocked: 'لم تُحفظ تعديلاتك بعد — انسخ عملك ثم حاول مرة أخرى',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    newCharacter: 'شخصية جديدة',
    newPlot: 'حبكة جديدة',
    exportPdf: 'تصدير PDF',
    stressTest: 'اختبار الضغط',
    aiMentor: 'المرشد الذكي',

    // Story Auditor
    storyAuditor: 'مراجع القصة',
    runStressTest: 'تشغيل الاختبار',
    storyHealthScore: 'درجة صحة القصة',
    issuesFound: 'المشكلات المكتشفة',
    suggestions: 'الاقتراحات',
    strengths: 'نقاط القوة',

    // Payment
    upgradeToPro: 'الترقية إلى Pro',
    monthly: 'شهري',
    yearly: 'سنوي',
    perMonth: '/شهر',
    perYear: '/سنة',
    subscribe: 'اشترك',

    // Messages
    loading: 'جارٍ التحميل...',
    saved: 'تم الحفظ',
    error: 'حدث خطأ ما',
    proFeature: 'ميزة Pro',

    // Forge Guide toggle
    showForgeGuide: 'إظهار دليل المصنع',
    hideForgeGuide: 'إخفاء دليل المصنع',

    // Forge Guide beat texts
    forgeGuide_firstFrame: 'هذه هي الانطباعة الأولى لعالمك. أرنا لقطةً لحياة بطلك قبل أن يتغيّر كل شيء. يجب أن تكون مرآةً بصريةً وعاطفيةً لمكانه في النهاية — لن يعرف الجمهور ذلك بعد، لكنك تعرفه أنت. اسأل نفسك: كيف تبدو الحياة الاعتيادية لهذا الشخص، وكم هو بعيد عمّن يجب أن يصبح؟',
    forgeGuide_coreTruth: 'في مكانٍ ما من فصلك الأول، يقول أحد الشخصيات — غالبًا ليس البطل — شيئًا هو العمود الفقري المحوري لقصتك كلها. ربما لن يوافق بطلك على ذلك الآن، لكنه سيفهمه تمامًا في النهاية. ما هي الحقيقة الوحيدة التي تحاول قصتك إثباتها؟',
    forgeGuide_worldBefore: 'قدّم بطلك في بيئته الطبيعية. نحتاج أن نرى عالمه وعلاقاته وعيوبه وحاجته غير المُلبّاة. ستة أشياء يجب إرساؤها هنا: الشخصية، والمخاطر، والنوع، والنبرة، والعيب الذي يجب على البطل التغلب عليه، والعالم الذي على وشك أن يتعطل. خذ وقتك — هذه اللحظة الأخيرة من السلام.',
    forgeGuide_spark: 'يحدث شيء لم يتسبب فيه البطل ولا يستطيع تجاهله. هذا هو الحدث الذي يطرق باب عالمه العادي ويطالب بإجابة. يجب أن يبدو مفاجئًا وحتميًا في آنٍ واحد. إنه ليس قرارًا — إنه الكون يتحرك. ما الذي يحدث لبطلك ويجعل القصة تبدأ؟',
    forgeGuide_crossroads: 'بطلك يعرف أن الشرارة اشتعلت، لكنه ليس مستعدًا للتصرف. هذا هو الصراع الداخلي — هل أذهب أم أبقى؟ كلما طال هذا الجدال وكان أكثر إيلامًا، كلما استثمر الجمهور أكثر. يحتاج بطلك إلى دفعة أخيرة — معلومة حاسمة أو تهديد أو لحظة شجاعة — ليلتزم أخيرًا. ما الذي يوقفه، وما الذي يدفعه أخيرًا للأمام؟',
    forgeGuide_leap: 'يتخذ بطلك قرارًا فاعلًا وواعيًا بالدخول إلى عالم جديد — بشكل حرفي أو مجازي. انتهت قصة الفصل الأول. تبدأ قصة جديدة. يجب أن يبدو هذا نقطة لا عودة منها. يجب أن يشعر الجمهور أن لا شيء سيكون كما كان. أين يتخطى بطلك العتبة، وماذا يترك وراءه؟',
    forgeGuide_heartThread: 'هذه هي حبكتك الفرعية العاطفية — عادةً علاقة تحمل المحور. بينما تدور القصة أ حول ما يريده البطل، فإن خيط القلب يتعلق بما يحتاجه. في الغالب مرشد أو اهتمام رومانسي أو صداقة غير متوقعة. ستعلّم هذه العلاقة بطلك الحقيقة المحورية في النهاية. من هذا الشخص وماذا يمثل مما يفتقده بطلك؟',
    forgeGuide_promise: 'هذا هو سبب شراء الناس للتذاكر. المتعة والإثارة والرومانسية والمخاوف — مهما وعد به نوعك، قدّمه هنا. بطلك في العالم الجديد يستكشفه. الأمور لا تسير بشكل مثالي لكنها لم تنهار بعد. هذا أكثر أقسام فيلمك ترفيهًا. هل تفي بالوعد الذي قطعه نوعك للجمهور؟',
    forgeGuide_turningTide: 'في منتصف قصتك، يتغير شيء مهم. إما أن يكون لدى بطلك انتصار زائف حيث تبدو الأمور رائعة لكنها على وشك الانهيار، أو هزيمة زائفة حيث تبدو الأمور فظيعة لكن البطل يجد قتاله. تتضاعف المخاطر هنا. يجب على بطلك التوقف عن رد الفعل والبدء في قيادة القصة. ما الذي يتغير في نقطة المنتصف مما يجعل النصف الثاني قصةً مختلفة تمامًا؟',
    forgeGuide_wallsClosing: 'بطلك يسعى وراء هدفه، لكن الآن كل قوة — داخلية وخارجية — تقاوم. الشك والأعداء وسوء الحظ والخيانة. يجب أن يبدو التوتر خانقًا. يجب أن يتساءل الجمهور حقًا إذا كان بطلك قادرًا على النجاح. هذه ليست مصاعب عشوائية — كل عقبة يجب أن تكشف عن عيب أعمق لم يواجهه البطل بعد. من أو ما الذي يقترب، ولماذا يؤلم أكثر الآن؟',
    forgeGuide_breakingPoint: 'خسر بطلك. ليس الهدف الخارجي فحسب — بل فقد ما كان يهمه عاطفيًا أكثر من أي شيء. علاقة أو اعتقاد أو مرشد أو جزء من نفسه. هذه أدنى لحظة في القصة. يجب أن تبدو نهائية. يجب أن يؤمن الجمهور، ولو للحظة، أنه لا طريق للعودة. ما هو أسوأ شيء يمكن أن يحدث لبطلك الآن — واجعله يحدث.',
    forgeGuide_hollow: 'بعد نقطة الانهيار، يجلس بطلك وسط الأنقاض. هذه لحظة هدوء قبل العاصفة. يجب أن يواجه الكذبة التي كان يعيشها ويختار — بوعي ومع ألم — احتضان الحقيقة المحورية. هذه روح قصتك. لا أحد يستطيع إنقاذه هنا. يجب أن ينقذ نفسه. ماذا يدرك بطلك في أحلك لحظاته وأكثرها صمتًا؟',
    forgeGuide_rising: 'يخرج بطلك متحوّلًا. مسلحًا بالحقيقة، يضع خطته النهائية. الأدوات القديمة لن تنفع — يجب أن يستخدم من أصبح، لا من كان. تبدأ القصة الرئيسية وخيط القلب في التقارب. المعركة النهائية قادمة، وللمرة الأولى، بطلك مستعد حقًا. ما هي الخطة، وكيف تعكس نموّه؟',
    forgeGuide_reckoning: 'ينفّذ بطلك الخطة في ذروة تختبر كل ما تعلّمه. يُثبَت المحور، ويُغلَب العيب، ويواجه الخصم — الداخلي أو الخارجي — وجهًا لوجه. يجب أن تنتهي قصة كل شخصية رئيسية هنا. اجعلها تستحق كل دقيقة استثمرها الجمهور. هل تبدو نهايتك مفاجئةً وحتميةً في آنٍ واحد؟',
    forgeGuide_lastFrame: 'صورة أخيرة تعكس الإطار الأول — لكن كل شيء تغيّر. نرى العالم بعد. بطلك تحوّل. وُفي وعد الحقيقة المحورية. يجب أن تدوم هذه الصورة. يجب أن تشعر كالنوتة الأخيرة لأغنية تنتهي بشكل مثالي. كيف يبدو عالم بطلك الآن بعد أن أصبح من كان مُقدَّرًا له أن يكون؟',

    // ── Common ──
    saving: 'جارٍ الحفظ...',
    close: 'إغلاق',
    copy: 'نسخ',
    copied: 'تم النسخ',
    previous: 'السابق',
    next: 'التالي',
    back: 'رجوع',
    generating: 'جارٍ التوليد...',

    // ── Dashboard ──
    dash_title: 'مشاريعي',
    dash_newProject: 'مشروع جديد',
    dash_noProjects: 'لا توجد مشاريع بعد — أنشئ أولها أدناه',
    dash_emptyTitle: 'لا توجد مشاريع بعد',
    dash_emptyDesc: 'أنشئ مشروعك الأول لتبدأ في بناء قصتك.',
    dash_createFirst: 'أنشئ مشروعك الأول',
    dash_lastEdited: 'آخر تعديل {{date}}',
    dash_progress: '{{pct}}% مكتمل',
    dash_projects_one: 'مشروع واحد',
    dash_projects_other: '{{count}} مشاريع',

    // ── Sidebar ──
    sidebar_back: 'رجوع',
    sidebar_backToProjects: 'العودة إلى مشاريعي',
    sidebar_plot: 'الحبكة',
    sidebar_chars: 'الشخصيات',
    sidebar_scenes: 'المشاهد',
    sidebar_script: 'السيناريو',
    sidebar_world: 'العالم',
    sidebar_web: 'الشبكة',
    sidebar_bible: 'الدليل',
    sidebar_mentor: 'المرشد',
    sidebar_char: 'الشخصية',
    sidebar_locked: 'يتطلب خطة Indie أو أعلى',

    // ── Navigation ──
    nav_backToDashboard: 'لوحة التحكم',
    nav_backToWorkspace: 'العودة إلى مساحة العمل',
    nav_backToPlot: 'العودة إلى الحبكة',

    // ── Plot Act titles ──
    plotAct1Title: 'الفصل الأول: الانطلاق',
    plotAct1Sub: 'الإعداد',
    plotAct2Title: 'الفصل الثاني: المواجهة',
    plotAct2Sub: 'التصاعد الدرامي',
    plotAct3Title: 'الفصل الثالث: الحل',
    plotAct3Sub: 'الذروة والحل',

    // ── Plot level UI ──
    plot_overallProgress: 'التقدم الإجمالي',
    plot_beatOf: 'الخطوة {{current}} من {{total}}',
    plot_buildScenes: 'بناء المشاهد',
    plot_complete: 'إتمام الحبكة',
    plot_nextBeat: 'الخطوة التالية',

    // ── Beat placeholders ──
    beatPh_opening_image: 'لقطة من عالم قصتك قبل بدء التغيير',
    beatPh_setup: 'قدّم البطل وعالمه وعلاقاته الأولية',
    beatPh_theme_stated: 'تلميح للدرس أو الحقيقة التي سيتعلمها البطل',
    beatPh_catalyst: 'الحدث المحرّك الذي يعطّل العالم الاعتيادي',
    beatPh_debate: 'البطل يتردد أو يتساءل عن الالتزام بالرحلة',
    beatPh_break_into_two: 'البطل يلتزم ويدخل عالمًا أو موقفًا جديدًا',
    beatPh_b_story: 'تقديم حبكة فرعية أو علاقة تعكس المحور',
    beatPh_fun_and_games: 'وعد الفكرة — ما جاء الجمهور لأجله',
    beatPh_midpoint: 'انتصار أو هزيمة كبرى يرفع المخاطر',
    beatPh_bad_guys_close_in: 'القوى الخارجية والداخلية تضيق على البطل',
    beatPh_all_is_lost: 'أدنى نقطة — هزيمة زائفة أو نكسة كبرى',
    beatPh_dark_night: 'البطل يعالج فشله قبل الانبعاث من جديد',
    beatPh_break_into_three: 'البطل يجد الحل أو المقاربة الجديدة',
    beatPh_finale: 'المواجهة الأخيرة حيث يُثبت البطل تحوّله',
    beatPh_final_image: 'مرآة الصورة الافتتاحية توضح مقدار ما تغيّر',
    beatPh_obstacle: 'الصراع أو التحدي الرئيسي يتصاعد',
    beatPh_escalation: 'المخاطر ترتفع، التعقيدات تتراكم',

    // ── Character Level titles ──
    charLevel0Title: 'المستوى 0: الهوية',
    charLevel0Sub: 'الأساس',
    charLevel1Title: 'المستوى 1: المخطط الداخلي',
    charLevel1Sub: 'الروح والأصل',
    charLevel2Title: 'المستوى 2: العالم والمقدمة',
    charLevel2Sub: 'الإعداد',
    charLevel3Title: 'المستوى 3: قدر الضغط',
    charLevel3Sub: 'الرحلة',
    charLevel4Title: 'المستوى 4: الحساب الأخير',
    charLevel4Sub: 'الحل',
    charAntag1Title: 'المستوى 1: الدافع والمنطق الداخلي',
    charAntag1Sub: 'سبب قتالهم',
    charAntag2Title: 'المستوى 2: الصلة بالبطل',
    charAntag2Sub: 'التعارض',
    charAntag3Title: 'المستوى 3: القوة والحضور',
    charAntag3Sub: 'التهديد والفاعلية',
    charAntag4Title: 'المستوى 4: التعقيد والإنسانية',
    charAntag4Sub: 'أبعد من كونه شريرًا',
    charSupp1Title: 'المستوى 1: لماذا',
    charSupp1Sub: 'الغرض والدور',
    charSupp2Title: 'المستوى 2: من هو',
    charSupp2Sub: 'الشخصية والعمق',
    charSupp3Title: 'المستوى 3: كيف',
    charSupp3Sub: 'الصوت والحضور',
    charSupp4Title: 'المستوى 4: النمو',
    charSupp4Sub: 'القوس والأثر',

    // ── Character question labels ──
    qLabel_name: 'الاسم',
    qLabel_age: 'العمر',
    qLabel_gender: 'الجنس',
    qLabel_ethnicity: 'العرق',
    qLabel_biography: 'سيرة ذاتية مختصرة',
    qLabel_the_lie: 'الكذبة',
    qLabel_the_truth: 'الحقيقة',
    qLabel_the_ghost: 'الشبح (اختياري للأفلام القصيرة)',
    qLabel_wants: 'الرغبات',
    qLabel_needs: 'الاحتياجات',
    qLabel_needs_ignored: 'الاحتياجات (المتجاهلة)',
    qLabel_corruption: 'الفساد',
    qLabel_mission: 'المهمة',
    qLabel_hero_narrative: 'روايته البطولية',
    qLabel_tangible_goal: 'الهدف الملموس',
    qLabel_human_roots: 'الجذور الإنسانية',
    qLabel_internal_logic: 'المنطق الداخلي',
    qLabel_defined_role: 'الدور المحدد',
    qLabel_plot_impact: 'الأثر على الحبكة',
    qLabel_the_trade: 'الصفقة',
    qLabel_normal_world: 'العالم الاعتيادي',
    qLabel_persona: 'الشخصية الظاهرة',
    qLabel_core_trait: 'السمة الأساسية',
    qLabel_characteristic_moment: 'اللحظة المميزة',
    qLabel_direct_conflict: 'الصراع المباشر',
    qLabel_mirror_effect: 'تأثير المرآة',
    qLabel_targeted_pressure: 'الضغط الموجّه',
    qLabel_the_wound: 'الجرح',
    qLabel_offscreen_goal: 'الهدف خارج الكادر',
    qLabel_personal_stakes: 'المخاطر الشخصية',
    qLabel_the_secret: 'السر',
    qLabel_moral_compass: 'البوصلة الأخلاقية',
    qLabel_inciting_event: 'الحدث المحرّك',
    qLabel_first_plot_point: 'نقطة الحبكة الأولى',
    qLabel_midpoint_char: 'نقطة المنتصف',
    qLabel_third_plot_point: 'نقطة الحبكة الثالثة',
    qLabel_legitimate_threat: 'التهديد الحقيقي',
    qLabel_proactive_agency: 'الفاعلية الاستباقية',
    qLabel_early_victories: 'الانتصارات المبكرة',
    qLabel_distinct_voice: 'الصوت المميز',
    qLabel_the_silhouette: 'الصورة الظلية',
    qLabel_vocal_anchor: 'المرساة الصوتية',
    qLabel_habit_tic: 'العادة / التصرف المتكرر',
    qLabel_entrance_exit: 'الدخول والخروج',
    qLabel_climax: 'الذروة',
    qLabel_new_normal: 'الواقع الجديد',
    qLabel_transformation: 'التحوّل',
    qLabel_redeeming_trait: 'الصفة المُنقِذة',
    qLabel_vulnerability: 'نقطة الضعف',
    qLabel_no_caricatures: 'لا شخصيات كاريكاتورية',
    qLabel_the_right_point: 'النقطة الصحيحة',
    qLabel_first_impression_vs_reality: 'الانطباع الأول مقابل الواقع',
    qLabel_pressure_point: 'نقطة الضغط',
    qLabel_the_ending: 'النهاية',

    // ── Character question placeholders ──
    qPh_name: 'الاسم الكامل للشخصية',
    qPh_age: 'كم عمرها؟',
    qPh_gender: 'هويتها الجندرية',
    qPh_ethnicity: 'الخلفية الثقافية',
    qPh_biography: 'من، ماذا، متى، أين، لماذا — نظرة عامة موجزة على حياتهم',
    qPh_the_lie: 'ما هو الاعتقاد الزائف الذي يعيق شخصيتك؟',
    qPh_the_lie_neg: 'ما هو الاعتقاد الزائف الذي سيقود إلى سقوطهم؟',
    qPh_the_truth: 'ما هي الحقيقة التي يجب أن يكتشفها؟',
    qPh_the_truth_flat: 'ما هي الحقيقة التي تمتلكها شخصيتك بالفعل؟',
    qPh_the_ghost: 'ما هي الصدمة الماضية التي خلقت الكذبة؟',
    qPh_the_ghost_neg: 'ما هي الصدمة الماضية التي تدفع سقوطهم؟',
    qPh_the_ghost_flat: 'ما هي التجربة التي رسّخت هذه الحقيقة؟',
    qPh_wants: 'ما الذي تعتقد شخصيتك أنها تريده؟',
    qPh_wants_neg: 'ما الذي تشتهيه شخصيتك؟',
    qPh_wants_flat: 'ما هو الهدف الخارجي الذي يدفعها؟',
    qPh_needs: 'ما الذي يحتاجونه فعلًا؟',
    qPh_needs_ignored: 'ما الذي يحتاجونه فعلًا لكنهم سيتجاهلونه؟',
    qPh_corruption: 'ما هو الإغراء الذي سيستسلمون له؟',
    qPh_mission: 'كيف سيشاركون حقيقتهم مع العالم؟',
    qPh_hero_narrative: 'كيف يرى الخصم نفسه بطلًا في قصته الخاصة؟',
    qPh_tangible_goal: 'ما هي النتيجة الملموسة التي يسعون إليها؟',
    qPh_human_roots: 'ما هي التجربة الإنسانية أو الألم الذي يحرّك هذه الشخصية؟',
    qPh_internal_logic: 'ما هو الكود الداخلي الذي يبرر أفعالهم؟',
    qPh_defined_role: 'ما هي الوظيفة السردية التي تؤديها هذه الشخصية؟',
    qPh_plot_impact: 'كيف يدفعون القصة للأمام مباشرةً؟',
    qPh_the_trade: 'ماذا يُعطون للبطل — وماذا يأخذون؟',
    qPh_normal_world: 'صف حياتهم الاعتيادية قبل الرحلة',
    qPh_persona: 'كيف يُقدّمون أنفسهم للعالم؟',
    qPh_core_trait: 'ما هي السمة المُعرِّفة التي يُظهرونها؟',
    qPh_characteristic_moment: 'مشهد يُظهر من هم الآن',
    qPh_direct_conflict: 'كيف تتعارض هذه الشخصية مباشرةً مع البطل؟',
    qPh_mirror_effect: 'كيف تعكس الجانب المظلم للبطل أو أسوأ مستقبل محتمل له؟',
    qPh_targeted_pressure: 'ما هو الضعف أو الخوف الذي يستغله في البطل؟',
    qPh_the_wound: 'ما هو الجرح الشخصي الذي جعل هذا الخصم على ما هو عليه؟',
    qPh_offscreen_goal: 'كيف تبدو حياتهم عندما لا يكونون في المشهد؟',
    qPh_personal_stakes: 'ما الذي يخاطرون بخسارته في صراع القصة؟',
    qPh_the_secret: 'ما الذي يخفونه — حتى عن البطل؟',
    qPh_moral_compass: 'ما الذي يعتقدون أنه صواب؟ أين خطّهم الأحمر؟',
    qPh_inciting_event: 'ما الذي يعطّل عالمهم الاعتيادي؟',
    qPh_first_plot_point: 'نقطة اللاعودة',
    qPh_midpoint_char: 'لحظة الحقيقة — كل شيء يتغير',
    qPh_third_plot_point: 'لحظة "الموت" — أدنى نقطة',
    qPh_legitimate_threat: 'لماذا تُشكّل هذه الشخصية خطرًا حقيقيًا على البطل؟',
    qPh_proactive_agency: 'كيف يدفعون الحبكة للأمام — لا مجرد الردّ على البطل؟',
    qPh_early_victories: 'صف لحظةً يفوز فيها الخصم أو يكسب أرضًا',
    qPh_distinct_voice: 'ما الذي يجعل حوارهم وحضورهم معروفَين فورًا؟',
    qPh_the_silhouette: 'صف مظهرهم — شيء بصري مميز فيهم',
    qPh_vocal_anchor: 'عبارة أو إيقاع أو نمط خطاب فريد لهم',
    qPh_habit_tic: 'سلوك متكرر أو إيماءة أو طقس يكشف عن الشخصية',
    qPh_entrance_exit: 'كيف يدخلون ويغادرون المشهد عادةً؟',
    qPh_climax: 'اختبار الحقيقة — المواجهة الأخيرة',
    qPh_new_normal: 'كيف تغيّر عالمهم؟',
    qPh_transformation: 'كيف تغيّروا كأشخاص؟',
    qPh_redeeming_trait: 'صفة حقيقية واحدة تجعلهم بشرًا — لا محبوبًا، فقط حقيقيًا',
    qPh_vulnerability: 'ما الذي يخترق درعهم؟ مم يخافون؟',
    qPh_no_caricatures: 'كيف تتجنب هذه الشخصية أن تكون شريرًا كاريكاتوريًا؟',
    qPh_the_right_point: 'ما هي الحقيقة، إن وُجدت، التي يحمل هذا الخصم وعلى البطل أو الجمهور مواجهتها؟',
    qPh_first_impression_vs_reality: 'كيف تختلف قراءتك الأولية لهم عمّن هم حقًا؟',
    qPh_pressure_point: 'ما هو الموقف الذي يُجبرهم على الكشف عن ذاتهم الحقيقية؟',
    qPh_the_ending: 'أين ينتهون؟ كيف غيّرتهم القصة — ولو قليلًا؟',

    // ── Scene Builder ──
    scene_builder: 'منشئ المشاهد',
    scene_newScene: 'مشهد جديد',
    scene_editScene: 'تعديل المشهد',
    scene_addFirst: 'إضافة أول مشهد',
    scene_noScenes: 'لا توجد مشاهد بعد',
    scene_noScenesDesc: 'أضف مشاهد لتقسيم خطوات قصتك إلى لحظات قابلة للتصوير.',
    scene_noScenesBeat: 'أضف مشاهد لـ"{{beat}}" لتقسيمها إلى لحظات قابلة للتصوير.',
    scene_titleLabel: 'عنوان المشهد',
    scene_titleRequired: 'عنوان المشهد *',
    scene_titlePh: 'داخلي. السوق — نهارًا',
    scene_locationLabel: 'الموقع',
    scene_locationPh: 'داخلي. سوق نيروبي — ساعة ذهبية',
    scene_plotProjectLabel: 'مشروع الحبكة',
    scene_beatLabel: 'الخطوة',
    scene_standalone: 'مستقل',
    scene_none: 'لا شيء',
    scene_bmoc: 'B.M.O.C',
    scene_actionLabel: 'الحدث — ما الذي يحدث جسديًا في هذا المشهد؟',
    scene_actionPh: 'صف الحدث الجسدي الرئيسي للمشهد',
    scene_wantVsNeedLabel: 'الرغبة مقابل الحاجة — ماذا تريد الشخصية، وما الذي تحتاجه فعلًا؟',
    scene_wantVsNeedPh: 'الرغبة الظاهرة مقابل الحاجة العميقة',
    scene_conflictLabel: 'الصراع — ما هي القوة (شخص أو موقف أو داخلي) التي تعارضها؟',
    scene_conflictPh: 'القوة المعارضة التي تقاوم',
    scene_turnLabel: 'التحوّل — كيف ينتهي المشهد بشكل مختلف عن بدايته؟',
    scene_turnPh: 'التحوّل في الموقف أو العاطفة أو المعرفة',
    scene_functionLabel: 'وظيفة المشهد',
    scene_fnSelectPh: '— اختر وظيفة —',
    scene_fnIntroduce: 'تقديم',
    scene_fnEscalate: 'تصعيد',
    scene_fnReveal: 'كشف',
    scene_fnConfront: 'مواجهة',
    scene_fnResolve: 'حل',
    scene_polarityShift: 'تحوّل القطبية',
    scene_polarityStartPh: 'أمل',
    scene_polarityEndPh: 'يأس',
    scene_notes: 'ملاحظات',
    scene_notesPh: 'ملاحظات الإنتاج، أفكار اللقطات، مراجع الأسلوب...',
    scene_saveScene: 'حفظ المشهد',
    scene_scenesCount: '{{count}} / {{limit}} مشاهد',
    scene_action_card: 'الحدث',
    scene_wantVsNeed_card: 'الرغبة مقابل الحاجة',
    scene_conflict_card: 'الصراع',
    scene_turn_card: 'التحوّل',
    scene_function_card: 'وظيفة المشهد',
    scene_beatPrefix: 'الخطوة: ',

    // ── Project Workspace ──
    ws_overallProgress: 'التقدم الإجمالي',
    ws_act1: 'الفصل الأول',
    ws_act2: 'الفصل الثاني',
    ws_act3: 'الفصل الثالث',
    ws_act1Desc: 'الإعداد',
    ws_act2Desc: 'التصاعد الدرامي',
    ws_act3Desc: 'الحل',
    ws_editAct: 'تعديل الفصل {{n}}',
    ws_completedBeats: '{{done}} / {{total}} خطوات',
    ws_aiLogline: 'Logline وملخص بالذكاء الاصطناعي',
    ws_aiLoglineDesc: 'أنشئ جملة تقديم وملخصًا ثلاثي الفصول من قائمة خطواتك.',
    ws_generateAI: 'أنشئ بالذكاء الاصطناعي',
    ws_generating: 'جارٍ التوليد...',
    ws_logline: 'Logline',
    ws_synopsis: 'الملخص',
    ws_copy: 'نسخ',
    ws_copied: 'تم النسخ',
    ws_3LeftThisMonth: '{{n}} من 3 متبقية هذا الشهر',
    ws_monthlyGenerations: '3 توليدات شهريًا',

    // ── Plot Mentor ──
    pm_title: 'مرشد الحبكة',
    pm_desc: 'تدريب ذكي على بنية حبكتك ومنطق القصة',
    pm_forgeGuide: 'دليل المصنع',
    pm_forgeGuideDesc: 'تدريب سقراطي — خطوةً بخطوة',
    pm_stressTest: 'اختبار الضغط',
    pm_stressTestDesc: 'تحدَّ الثغرات وأوجه الخلل المنطقي',
    pm_startForgeGuide: 'ابدأ دليل المصنع',
    pm_runStressTest: 'شغّل اختبار الضغط',
    pm_readingPlot: 'جارٍ قراءة بيانات حبكتك...',
    pm_thinking: 'جارٍ التفكير...',
    pm_replyPh: 'ردّ على مرشدك...',
    pm_fullStory: 'القصة كاملة',
    pm_act1: 'الفصل الأول',
    pm_act2: 'الفصل الثاني',
    pm_act3: 'الفصل الثالث',
    pm_usesLeft: '{{n}} من 3 متبقية هذا الشهر',
    pm_close: 'إغلاق',

    // ── AI Mentor UI ──
    mentor_replyPh: 'اسأل مرشدك...',
    mentor_thinking: 'مرشدك يفكّر...',
    mentor_proFeature: 'المرشد الذكي ميزة Pro',
    mentor_upgradeBtn: 'الترقية إلى Pro',
    mentor_topicTrauma: 'الصدمة',
    mentor_topicLie: 'الكذبة',
    mentor_topicTruth: 'الحقيقة',
    mentor_topicWants: 'الرغبات',
    mentor_topicNeeds: 'الاحتياجات',
    mentor_topicRelationships: 'العلاقات',
    mentor_topicConflict: 'الصراع',
    mentor_level_identity: 'الهوية',
    mentor_level_internalBlueprint: 'المخطط الداخلي',
    mentor_level_worldIntro: 'العالم والمقدمة',
    mentor_level_pressureCooker: 'قدر الضغط',
    mentor_level_finalReckoning: 'الحساب الأخير',

    // ── Export ──
    export_title: 'تصدير الدليل القصصي',
    export_sections: 'الأقسام',
    export_filename: 'اسم الملف',
    export_download: 'تنزيل PDF',
    export_download_as: 'تنزيل {{format}}',
    export_downloading: 'جارٍ التوليد…',
    export_preview: 'معاينة',
    export_noSections: 'اختر أقسامًا من اليسار لمعاينة المحتوى.',

    // ── Character Level chrome ──
    char_roleDesc: 'يحدد موضع هذه الشخصية في الشبكة',
    char_arcTypeDesc: 'يحدد أسئلة المستوى 1 للمخطط الداخلي',
    char_completeContinue: 'أكمل وتابع',
    char_completeCharacter: 'أكمل الشخصية',
    char_enneagramLabel: 'اختبار ضغط الإنياغرام',
    char_enneagramPh: 'اختر النمط الشخصي',
    char_upgradeToIndie: 'الترقية إلى Indie',
    char_enneagramSyncNote: 'سيزامن أنماط الضغط مع خطوة "نقطة الانهيار" في مصنع الحبكة',
    char_enneagramLockedNote: 'افتح مزامنة الشخصية بين لوحات التحكم مع خطة Indie أو أعلى',

    // ── Enneagram types ──
    enn_type1: 'النوع 1: الكمالي',
    enn_type2: 'النوع 2: المساعِد',
    enn_type3: 'النوع 3: المنجِز',
    enn_type4: 'النوع 4: الفردانيّ',
    enn_type5: 'النوع 5: المحقّق',
    enn_type6: 'النوع 6: الوفيّ',
    enn_type7: 'النوع 7: المتحمّس',
    enn_type8: 'النوع 8: المتحدّي',
    enn_type9: 'النوع 9: صانع السلام',

    // ── Arc Helper content ──
    arcHelper_positive_title: 'قوس التحوّل (إيجابي)',
    arcHelper_positive_desc: 'شخصيتك تتغلب على كذبتها وتحتضن الحقيقة، فتنمو كإنسان.',
    arcHelper_positive_g0: 'الكذبة هي اعتقاد زائف يعيقها',
    arcHelper_positive_g1: 'الحقيقة هي ما يجب أن تكتشفه للنمو',
    arcHelper_positive_g2: 'الشبح هو الحدث الماضي الذي خلق الكذبة',
    arcHelper_positive_g3: 'تريد شيئًا خارجيًا، لكنها تحتاج نموًا داخليًا',
    arcHelper_positive_t0: 'اجعل الكذبة تبدو حقيقية لشخصيتك',
    arcHelper_positive_t1: 'يجب أن تتناقض الحقيقة مباشرةً مع الكذبة',
    arcHelper_positive_t2: 'غالبًا تتعارض رغبتها وحاجتها',
    arcHelper_negative_title: 'قوس السقوط (سلبي)',
    arcHelper_negative_desc: 'شخصيتك تستسلم لكذبتها مما يؤدي إلى سقوطها أو فسادها.',
    arcHelper_negative_g0: 'تصبح الكذبة مبررهم للخيارات السيئة',
    arcHelper_negative_g1: 'الفساد هو الإغراء الذي لا يستطيعون مقاومته',
    arcHelper_negative_g2: 'الشبح يغذّي انحدارهم',
    arcHelper_negative_g3: 'يتجاهلون ما يحتاجونه فعلًا',
    arcHelper_negative_t0: 'اجعل الفساد مفهومًا',
    arcHelper_negative_t1: 'أظهر ما كان يمكن أن يكونوا عليه',
    arcHelper_negative_t2: 'المأساة تكمن فيما يخسرونه',
    arcHelper_flat_title: 'القوس الثابت',
    arcHelper_flat_desc: 'شخصيتك تعرف الحقيقة بالفعل وتستخدمها لتغيير العالم من حولها.',
    arcHelper_flat_g0: 'تمتلك حقيقةً راسخة لا تتزعزع',
    arcHelper_flat_g1: 'الشبح رسّخ هذه الحقيقة',
    arcHelper_flat_g2: 'مهمتهم مشاركة هذه الحقيقة',
    arcHelper_flat_g3: 'العقبات الخارجية تختبر قناعتهم',
    arcHelper_flat_t0: 'العالم يتحدى حقيقتهم',
    arcHelper_flat_t1: 'شخصيات أخرى قد تتغير بسببهم',
    arcHelper_flat_t2: 'يجب أن تُختبر قناعتهم لكن لا تُكسر',

    // ── ProjectWorkspace ──
    ws_setup: 'الإعداد',
    ws_loglineSynopsis: 'Logline والملخص',
    ws_addLogline: 'أضف Logline والملخص',
    ws_buildBeatSheet: 'ابنِ قائمة خطواتك فصلًا بفصل.',
    ws_allCharacters: 'جميع الشخصيات في مكتبتك.',
    ws_noCharacters: 'لا توجد شخصيات بعد',
    ws_noCharactersDesc: 'أضف شخصيات لتعمير قصتك.',
    ws_createFirstChar: 'أنشئ أول شخصية',
    ws_scenesDesc: 'المشاهد المرتبطة بهذه الحبكة.',
    ws_noScenes: 'لا توجد مشاهد بعد',
    ws_noScenesDesc: 'ابنِ مشاهد لكل خطوة في حبكتك.',
    ws_openSceneBuilder: 'افتح منشئ المشاهد',
    ws_indieFeature: 'ميزة Indie',
    ws_webIndieDesc: 'شبكة الشخصيات متاحة بخطة Indie وما فوق.',
    ws_webDesc: 'خطّط العلاقات والحجج المحورية بين الشخصيات.',
    export_sparkNote: 'Spark — PDF يتضمن علامة مائية',
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en,
      sw,
      es,
      fr,
      it,
      pt,
      ar,
    },
    lng: localStorage.getItem('pangaji_language') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

/**
 * Every language the app knows about, in picker order.
 *
 * `reviewed` is the ship gate, and it is deliberately not a feature flag: a
 * language only becomes selectable once a native speaker has signed off on
 * its strings. Drafts live in i18n/drafts/*.json and are NOT bundled or
 * registered as i18next resources, so an unreviewed language cannot be
 * reached even by setting localStorage by hand — there is nothing to load.
 *
 * es/it/pt are listed for the same reason from the other direction: they are
 * registered resources but sit at 117/482 keys (24%), so a user who reached
 * them would hit English fallback on three screens out of four.
 */
export const allLanguages = [
  { code: 'en',  name: 'English',     flag: '🇬🇧', reviewed: true },
  { code: 'sw',  name: 'Kiswahili',   flag: '🇰🇪', reviewed: true },
  { code: 'fr',  name: 'Français',    flag: '🇫🇷', reviewed: true },
  { code: 'ar',  name: 'العربية',      flag: '🇸🇦', reviewed: true },
  { code: 'es',  name: 'Español',     flag: '🇪🇸', reviewed: false },
  { code: 'it',  name: 'Italiano',    flag: '🇮🇹', reviewed: false },
  { code: 'pt',  name: 'Português',   flag: '🇵🇹', reviewed: false },
  // Post-launch Nigerian set — drafted, awaiting native review.
  { code: 'yo',  name: 'Yorùbá',      flag: '🇳🇬', reviewed: false },
  { code: 'ig',  name: 'Asụsụ Igbo',  flag: '🇳🇬', reviewed: false },
  { code: 'pcm', name: 'Naijá',       flag: '🇳🇬', reviewed: false },
];

/** What the picker actually offers. */
export const supportedLanguages = allLanguages.filter((l) => l.reviewed);

export default i18n;
